/* eslint-disable @typescript-eslint/no-dynamic-delete */
/* eslint-disable @typescript-eslint/require-await */

// @ts-strict-ignore

/* eslint-disable no-case-declarations */
/* eslint-disable no-async-promise-executor */

/** @module Webinar */

import { Subject, Subscription } from 'rxjs';
import { MainService } from 'rainbow-web-sdk/services/core/main.service';
import { AuthService } from 'rainbow-web-sdk/services/core/auth.service';
import { LoggerService } from 'rainbow-web-sdk/services/core/logger.service';
import { ErrorHelperService } from 'rainbow-web-sdk/services/utilities/errorHelper.service';
import { XmppService } from 'rainbow-web-sdk/services/core/xmpp.service';
import { SettingsService } from 'rainbow-web-sdk/services/core/settings.service';
import { ContactService } from 'rainbow-web-sdk/services/core/contact.service';

import { SpeechEnhancementService } from 'rainbow-web-sdk/services/speechEnhancement/speech-enhancement.service';
import { UtilitiesService } from "rainbow-web-sdk/services/utilities/utilities.service";
import { WebrtcConfigAndUtils } from "rainbow-web-sdk/services/webrtc/webrtcConfigAndUtils";
import { SpeechRecognitionService } from "rainbow-web-sdk/services/speechRecognitionService/speechRecognition.service";
import { DeviceManagementEvents, DeviceManagementServiceRB } from "rainbow-web-sdk/services/deviceManagement/deviceManagement.service";
import type { WebinarServiceRB } from "rainbow-web-sdk/services/webinars/webinars.service";
import { WebinarServiceEvents } from "rainbow-web-sdk/services/webinars/webinars.service";
import { BubbleServiceRB } from 'rainbow-web-sdk/services/bubbles/bubble.service';
import type { VirtualBackgroundService } from 'rainbow-web-sdk/services/virtualBackground/virtualBackground.service';
import { VIRTUAL_BACKGROUND_SVC } from 'rainbow-web-sdk/services/virtualBackground/virtualBackground.service';
import { ConnectedUser, Webinar, WebinarCodecs, WebinarEvents, WebinarMediaType, WebinarSession, WebinarSessionParticipant, WebinarSessionParticipantEvents, WebinarSessionParticipantMedia, WebinarSessionParticipantType, WebinarStatus } from "rainbow-web-sdk/models";
import { WebinarTelemetryService } from "rainbow-web-sdk/services";
import { RBEvent } from 'rainbow-web-sdk/models/event.model';
import { Contact, UserType } from 'rainbow-web-sdk/models/contact.model';
import { BubbleRB } from 'rainbow-web-sdk/models/bubble.model';
import { EventService } from 'rainbow-web-sdk/services/core/event.service';
import { PresenceHelperService } from 'rainbow-web-sdk/services/core/presenceHelper.service';
import { ProfileService } from 'rainbow-web-sdk/services/core/profile.service';
import { PromiseQueue } from 'rainbow-web-sdk/utils/promiseQueue';
import { WebinarAudioMixer } from './webinarAudioMixer';

import { wait } from 'rainbow-web-sdk';
import { JingleSession } from 'rainbow-web-sdk/libs/strophe/strophe.jingle.session';
import { $msg } from 'strophe.js';
import { stringifyError } from '../../utils/stringifyError';
import { AudioMixerSession } from '../../utils/audioMixer';
import { UserService, UserServiceRB } from '../users/user.service';

export type WebinarConstraintResolution = 'high' | 'medium' | 'low' | 'min';

export interface IWebinarConferenceService {
    joinWebinar(webinar: Webinar): Promise<Webinar>;
    leaveWebinar(webinar: Webinar): Promise<Webinar>;
    addAudioVideoMedia(webinar: Webinar, mediaType: string, stream?: MediaStream, replaceBackground?: boolean): Promise<Webinar>;
    addSharingMedia(webinar: Webinar, stream: MediaStream): Promise<Webinar>;
    removeLocalMedia(webinar: Webinar, mediaType: string): Promise<Webinar>;
    muteParticipant(webinar: Webinar, participant: WebinarSessionParticipant, mute: boolean): Promise<Webinar>;
    putOnStage(webinar: Webinar, participantId: string, media: WebinarMediaType, onStage: boolean, master?: boolean, fullStream?: boolean): Promise<Webinar>;
    shareAudioConfig(webinar: Webinar, audioConfig: string): Promise<Webinar>;
    startWebinar(webinar: Webinar): Promise<Webinar>;
    allowAttendeeToTalk(webinar: Webinar, userId: string): Promise<Webinar>;
    disableAttendeeToTalk(webinar: Webinar, userId: string): Promise<Webinar>;
    askForStaging(webinar: Webinar, participant: WebinarSessionParticipant, media: string): Promise<any>;
    declineStagingInvitation(webinar: Webinar, participant: WebinarSessionParticipant, media: string): Promise<void>;
    lowerHand(webinar: Webinar, userId: string): Promise<Webinar>;
    raiseHand(webinar: Webinar, userId: string): Promise<Webinar>;
    isActiveWebinar(webinar: Webinar): boolean;
    hasActiveWebinarSession(): boolean;
    disableIncomingStreams(webinar: Webinar, media: 'video' | 'sharing', disabled: boolean): Webinar;
    pauseVideoStream(webinar: Webinar, participant: WebinarSessionParticipant, media: 'video' | 'sharing', paused: boolean, origin: string): Promise<Webinar>;
    waitForGuestXmppReconnection(): Promise<void>;
}

/**
 * This service manages webinar conferences in Rainbow.
 */
export class WebinarConferenceService implements IWebinarConferenceService {

    public startConferencePromiseResolvers: any = {};
    public activeWebinar: Webinar | null = null;
    public activeWebinarSubject: Subject<any> = new Subject<any>();
    public audioMixer: WebinarAudioMixer;

    private connected: boolean = true;
    private reconnectingSessionIds: boolean[] = [];

    private mainService: MainService = MainService.getInstance();
    private authService: AuthService = AuthService.getInstance();
    private logger: LoggerService = LoggerService.getInstance();
    private xmppService: XmppService = XmppService.getInstance();
    private contactService: ContactService = ContactService.getInstance();
    private userService: UserService = UserServiceRB.getInstance();
    private errorHelperService: ErrorHelperService = ErrorHelperService.getInstance();
    private speechEnhancementService: SpeechEnhancementService = SpeechEnhancementService.getInstance();
    private eventService: EventService = EventService.getInstance();
    private webrtcConfigAndUtils: WebrtcConfigAndUtils = WebrtcConfigAndUtils.getInstance();
    private speechRecognitionService: SpeechRecognitionService = SpeechRecognitionService.getInstance();
    private utilitiesService: UtilitiesService = UtilitiesService.getInstance();
    private deviceManagementService: DeviceManagementServiceRB = DeviceManagementServiceRB.getInstance();
    private settingsService: SettingsService = SettingsService.getInstance();
    private presenceHelperService: PresenceHelperService = PresenceHelperService.getInstance();
    private profileService: ProfileService = ProfileService.getInstance();
    private bubbleService: BubbleServiceRB = BubbleServiceRB.getInstance();
    private webinarTelemetryService: WebinarTelemetryService = WebinarTelemetryService.getInstance();

    private virtualBackgroundService?: VirtualBackgroundService;

    private groupChatListener: any = null;
    private conferenceListener: any = null;
    private xmppJingleListener: any = null;
    private xmppServiceSubscription: Subscription;
    private videoServiceSubscription: Subscription;
    private xmppConnectionSubscription: Subscription;
    private replaceBackgroundSubscription: Subscription;
    private deviceManagementSubscription: Subscription;
    private webinarExtendedBackstage: boolean = false;

    private xmppStagingListener: any = null;
    public isInteractEventSent: boolean = false;
    private subscribeToPublisherPromiseResolvers: any = {};
    private subscribeToPublisherTimeouts: any = {};
    private audioMixerSession?: AudioMixerSession;
    private statsIntervals: any = {};
    private promiseQueue: PromiseQueue = PromiseQueue.create();
    private connectedUser!: ConnectedUser;

    /**
     * WebinarConferenceService static factory
     * @param webinarService - the webinarService parent
     */
    public static create(webinarService: WebinarServiceRB): WebinarConferenceService {
        return new WebinarConferenceService(webinarService);
    }

    /**
     * Private constructor method
     * @param webinarService - the webinarService parent
     */
    private constructor(private webinarService: WebinarServiceRB) {
        this.virtualBackgroundService = this.mainService.inject<VirtualBackgroundService>(VIRTUAL_BACKGROUND_SVC);
        this.audioMixer = new WebinarAudioMixer(this);
        this.webinarExtendedBackstage = true; //this.profileService.isFeatureEnabled(ProfileFeatures.WEBINAR_EXTENDED_BACKSTAGE);
        this.connectedUser = this.userService.connectedUser;
    }

    /**
     * Reconnection method, should be call when webinarService is reconnected
     */
    public async reconnect(): Promise<void> {

        /* Reattach listener to new XmppSession */
        this.attachXmppListeners();

        /* If webinar session exists before deconnection get update instance of this webinar */
        if (this.activeWebinar?.session) {

            // Get webinar snapshot
            await this.getWebinarSnapshot(this.activeWebinar);
            if (this.activeWebinar.room) {
                await this.bubbleService.updateRoomFromServer(this.activeWebinar.room);
                this.logger.info(`[WebinarService] reconnect (web: ${this.activeWebinar.id}) -- get webinar room`);
            }
            if (this.activeWebinar.practiceRoom) {
                await this.bubbleService.updateRoomFromServer(this.activeWebinar.practiceRoom as BubbleRB);
                this.logger.info(`[WebinarService] reconnect (web: ${this.activeWebinar.id}) -- get webinar practiceRoom`);
            }

            // Handle case where user is disconnected
            if (!this.activeWebinar.session.connected) {
                this.removeAudioVideoMedia(this.activeWebinar);
                this.presenceHelperService.resetBusyStateIfNeeded();

                if (this.activeWebinar.isAttendee) {
                    this.logger.info(`[WebinarService] reconnect (web: ${this.activeWebinar.id}) -- user not in webinar -- join`);
                    this.activeWebinar.session.localParticipant.allowedToTalk = false;
                    await this.webinarService.conference.joinConference(this.activeWebinar);
                }
                else {
                    this.logger.info(`[WebinarService] reconnect (web: ${this.activeWebinar.id}) -- user not in webinar -- disconect`);
                    this.activeWebinar.session = null;
                    this.webinarService.conference.removeAudioVideoMedia(this.activeWebinar);
                    this.webinarService.conference.removeSharingMedia(this.activeWebinar);
                    await this.webinarService.updateWebinarFromServer(this.activeWebinar);
                    this.activeWebinar.rxInfoSubject.next(RBEvent.create('error', 'webinarDisconnectedMoreThan90s'));
                }
            }

            // Handle case where webinar is terminated
            else if (this.activeWebinar.status === WebinarStatus.TERMINATED) {
                this.logger.info(`[WebinarService] reconnect (web: ${this.activeWebinar.id}) -- active webinar is terminated -- update media and presence`);
                this.clearWebinar(this.activeWebinar);
                this.presenceHelperService.resetBusyStateIfNeeded();
                this.activeWebinar = null;
            }

            // Webinar still active and user still connected
            else {
                this.logger.info(`[WebinarService] reconnect (web: ${this.activeWebinar.id}) -- active webinar`);

                // Reconnect local sessions if necessary
                this.scheduleTransportReplace(this.activeWebinar, this.activeWebinar.session.localParticipant.audioVideoSession);
                this.scheduleTransportReplace(this.activeWebinar, this.activeWebinar.session.localParticipant.sharingSession);

                // Reconnect remote sessions if necessary
                this.activeWebinar.session.speakerParticipants.forEach((speaker: WebinarSessionParticipant) => {
                    this.scheduleSubscribeToPublisher(this.activeWebinar, speaker, speaker.audioVideoSession, true);
                    this.scheduleSubscribeToPublisher(this.activeWebinar, speaker, speaker.sharingSession, true);
                });
            }
        }

        // Update Webinars from server
        await this.updateWebinarsFromServer();
        if (this.activeWebinar) this.activeWebinar.sendEvent(WebinarEvents.ON_DATA_UPDATE);
        this.webinarService.submitEvent(WebinarServiceEvents.WEBINARS_UPDATE);
    }

    private async updateWebinarsFromServer(trials: number = 1): Promise<void> {
        try {
            this.logger.info(`[WebinarService] updateWebinarsFromServer (trials: ${trials})`);
            const url = `${config.restServerUrl}/api/rainbow/webinar/v1.0/webinars?limit=100&offset=0`;
            const headers = this.authService.getRequestHeader();
            const response = await fetch(url, { method: "GET", headers });
            if (!response.ok) throw response;
            const data = (await response.json()).data;
            await this.webinarService.getActiveAndFutureWebinarsRooms(data);
            await this.utilitiesService.asyncForEach(data, async (webinarData: any) => {
                const webinar = this.webinarService.getWebinarById(webinarData.id);
                if (webinar) {
                    webinar.updateFromData(webinarData);
                    this.logger.info(`[WebinarService] updateWebinarsFromServer -- found existing webinar ${webinar.id} -- update it`);
                }
                else {
                    try {
                        await this.webinarService.getWebinar(webinarData.id);
                        this.logger.info(`[WebinarService] updateWebinarsFromServer -- found new webinar ${webinarData.id} -- get it from server -- success`);
                    }
                    catch {
                        this.logger.error(`[WebinarService] updateWebinarsFromServer -- found new webinar ${webinarData.id} -- get it from server -- failure`);
                    }
                }
            });
        }
        catch (error) {
            if (trials >= 5) { this.logger.error(`[WebinarService] updateWebinarsFromServer -- failure -- ${error.message}`); }
            else {
                await wait(1000);
                await this.updateWebinarsFromServer(++trials);
            }
        }
    }

    /**
     * Attach all listener to different service, should be called by the webinarService attachListener method
     */
    public attachAllListeners(): void {
        this.logger.debug('[WebinarService] attachAllListeners');
        this.attachXmppListeners();
        if (this.xmppServiceSubscription) { this.xmppServiceSubscription.unsubscribe(); this.xmppServiceSubscription = null; }
        this.xmppServiceSubscription = this.xmppService.connection.jingle.rxSubject.subscribe((event: RBEvent) => { this.promiseQueue.add(() => this.onSessionMessage(event)); });

        if (this.videoServiceSubscription) { this.videoServiceSubscription.unsubscribe(); this.videoServiceSubscription = null; }
        this.videoServiceSubscription = this.webrtcConfigAndUtils.subscribe((event: RBEvent) => { this.onVideoServiceEvent(event); });

        if (this.xmppConnectionSubscription) { this.xmppConnectionSubscription.unsubscribe(); this.xmppConnectionSubscription = null; }
        this.xmppConnectionSubscription = this.xmppService.subscribeToConnectionEvents((event: RBEvent) => { this.onXmppConnectionChangeEvent(event); });

        if (this.deviceManagementSubscription) { this.deviceManagementSubscription.unsubscribe(); this.deviceManagementSubscription = null; }
        this.deviceManagementSubscription = this.deviceManagementService.subscribe((event: RBEvent) => { this.onMediaDeviceChangeEvent(event); });

        if (this.virtualBackgroundService) {
            if (this.replaceBackgroundSubscription) { this.replaceBackgroundSubscription.unsubscribe(); this.replaceBackgroundSubscription = null; }
            this.replaceBackgroundSubscription = this.virtualBackgroundService.subscribe((event: RBEvent) => { this.onReplaceBackgroundChangeEvent(event); });
        }
    }

    public detachAllListeners() {
        this.logger.debug('[WebinarService] detachAllListeners');
        if (this.xmppServiceSubscription) { this.xmppServiceSubscription.unsubscribe(); this.xmppServiceSubscription = null; }
        if (this.videoServiceSubscription) { this.videoServiceSubscription.unsubscribe(); this.videoServiceSubscription = null; }
        if (this.xmppConnectionSubscription) { this.xmppConnectionSubscription.unsubscribe(); this.xmppConnectionSubscription = null; }
        if (this.deviceManagementSubscription) { this.deviceManagementSubscription.unsubscribe(); this.deviceManagementSubscription = null; }
        if (this.replaceBackgroundSubscription) { this.replaceBackgroundSubscription.unsubscribe(); this.replaceBackgroundSubscription = null; }
        if (this.groupChatListener) { this.xmppService.deleteHandler(this.groupChatListener); this.groupChatListener = null; }
        if (this.conferenceListener) { this.xmppService.deleteHandler(this.conferenceListener); this.conferenceListener = null; }
        if (this.xmppJingleListener) { this.xmppService.deleteHandler(this.xmppJingleListener); this.xmppJingleListener = null; }
        if (this.xmppStagingListener) { this.xmppService.deleteHandler(this.xmppStagingListener); }
    }

    private attachXmppListeners(): void {
        this.logger.debug(`[WebinarService] attachXmppListeners`);
        if (this.groupChatListener) { this.xmppService.deleteHandler(this.groupChatListener); this.groupChatListener = null; }
        this.groupChatListener = this.xmppService.addHandler((stanza: any) => { this.promiseQueue.add(() => this.onGroupchatMessage(stanza)); return true; }, "jabber:iq:notification", "message", "groupchat");

        if (this.conferenceListener) { this.xmppService.deleteHandler(this.conferenceListener); this.conferenceListener = null; }
        this.conferenceListener = this.xmppService.addHandler((stanza: any) => { this.promiseQueue.add(() => this.onConferenceMessage(stanza)); return true; }, "jabber:iq:conference:2", "message", null);

        if (this.xmppJingleListener) { this.xmppService.deleteHandler(this.xmppJingleListener); this.xmppJingleListener = null; }
        this.xmppJingleListener = this.xmppService.addHandler((stanza: any) => { this.promiseQueue.add(() => this.onXmppJingleMessage(stanza)); return true; }, 'urn:xmpp:jingle-message:0', 'message', null);

        if (this.xmppStagingListener) { this.xmppService.deleteHandler(this.xmppStagingListener); }
        this.xmppStagingListener = this.xmppService.addHandler((stanza: any) => { this.promiseQueue.add(() => this.onAskStagingMessage(stanza)); return true; }, 'http://jabber.org/protocol/commands', "message");

    }

    /**
     * Allow to join a webinarSession (entering the conference and established the webrtc connection)
     * @param webinar - the webinar to join
     */
    public async joinWebinar(webinar: Webinar): Promise<Webinar> {
        const localParticipantId = this.connectedUser.dbId;
        const correlatorId = Math.random().toString(36).slice(2, 12);
        const role = webinar.getConnectedUserRole();
        try {
            if (webinar.session) this.logger.info(`[WebinarService] joinWebinar (web: ${webinar.id}, part: ${localParticipantId}, role: ${role}, correlatorId: ${correlatorId}) -- conference already exists`);
            if (!webinar.session) {
                this.logger.debug(`[WebinarService] joinWebinar (web: ${webinar.id}, part: ${localParticipantId}, role: ${role}, correlatorId: ${correlatorId}) -- no conference info -- try to get from bubble session info`);
                this.webinarService.createWebinarSession(webinar, 'join');
            }
            if (!webinar.session && (webinar.isOrganizer || webinar.isSpeaker)) {
                this.logger.info(`[WebinarService] joinWebinar (web: ${webinar.id}, part: ${localParticipantId}, role: ${role}, correlatorId: ${correlatorId}) -- no info in room -- orga or speaker -- start a new conference`);
                await wait(2);
                await this.startConference(webinar);
            }
            else {
                this.logger.info(`[WebinarService] joinWebinar (web: ${webinar.id}, part: ${localParticipantId}, role: ${role}, correlatorId: ${correlatorId})`);
            }
            if (webinar.session && !webinar.session.localParticipant) {
                webinar.session.localParticipant = WebinarSessionParticipant.create(localParticipantId);
                webinar.session.localParticipant.contact = this.webinarService.userContact;
                try {
                    await wait(2);
                    await this.joinConference(webinar);
                }
                catch (error) {
                    // Handle switch device
                    if (error.message === 'alreadyConnected') throw error;
                    if ((error.errorDetailsCode === 403615 || error.errorDetailsCode === 403620) && error.feature === "WEBINAR_MAX_PARTICIPANTS") {
                        const errorMsg = JSON.parse(error.errorDetails)?.errorMsg;
                        this.logger.error(`[WebinarService] joinWebinar (web: ${webinar.id}, part: ${localParticipantId}, role: ${role}, correlatorId: ${correlatorId}) -- failure -- ${errorMsg}`);
                        // Reset the session localParticipant to re-enter in this part of code on the following joins
                        webinar.session.localParticipant = undefined;
                        throw error;
                    }
                    if (error.errorDetailsCode === 403620 && error.feature === "WEBINAR_MAX_ORGANIZERS") {
                        const errorMsg = JSON.parse(error.errorDetails)?.errorMsg;
                        this.logger.error(`[WebinarService] joinWebinar (web: ${webinar.id}, part: ${localParticipantId}, role: ${role}, correlatorId: ${correlatorId}) -- failure -- ${errorMsg}`);
                        // Reset the session localParticipant to re-enter in this part of code on the following joins
                        webinar.session.localParticipant = undefined;
                        throw error;
                    }
                    else {
                        this.logger.error(`[WebinarService] joinWebinar (web: ${webinar.id}, part: ${localParticipantId}, role: ${role}, correlatorId: ${correlatorId}) -- failure -- ${error?.message}`);
                        webinar.session.localParticipant = undefined;
                        throw error;
                    }
                }
            }
            if (webinar.session) {
                this.activeWebinar = webinar;
                this.activeWebinarSubject.next(RBEvent.create('ON_ACTIVE_WEBINAR', { webinar }));
                this.setBusyState(webinar.session.localParticipant);
            }
            return webinar;
        }
        catch (error) {
            if (error.message !== 'alreadyConnected' && !((error.errorDetailsCode === 403615 || error.errorDetailsCode === 403620) && error.feature === "WEBINAR_MAX_PARTICIPANTS"))
                this.logger.error(`[WebinarService] joinWebinar (web: ${webinar.id}, part: ${localParticipantId}, role: ${role}, correlatorId: ${correlatorId}) -- ${error.message}`);
            throw error;
        }
    }

    /**
     * Allow to leave a webinar
     *  - remove the user from the stage (if it was on the stage)
     *  - remove the user from the conference (stop the session if last user)
     * @param webinar - the webinar to leave
     * @param forced - when set to true this method stop the session (even if not the last user connect in conf)
     * @param nowait - when set to true this method call async child methods without waiting the end of execution (used in last chance deconnection event mechanism (onunload event))
     * @returns A promise that is resolved when the user has join the conference
     */
    public async leaveWebinar(webinar: Webinar, forced: boolean = false, nowait: boolean = false, reason: string = "Unknown"): Promise<Webinar> {
        const localParticipantId = this.connectedUser.dbId;
        const correlatorId = Math.random().toString(36).slice(2, 12);
        try {
            this.logger.info(`[WebinarService] leaveWebinar (web: ${webinar.id}, part: ${localParticipantId}, reason: ${reason}, correlatorId: ${correlatorId})`);

            if (!webinar.session) {
                this.logger.debug(`[WebinarService] leaveWebinar (web: ${webinar.id}, part: ${localParticipantId}, reason: ${reason}, correlatorId: ${correlatorId}) -- success -- no session`);
                return webinar;
            }

            if (nowait) {
                //this.putOnStage(webinar, this.webinarService.userContact.dbId, WebinarMediaType.participant, false);
                if (forced) this.stopSession(webinar);
                else this.disconnectParticipant(webinar, this.webinarService.userContact.dbId);
            }
            else {
                if (forced) { try { await this.stopSession(webinar); } catch { /**/ } }
                else { try { await this.disconnectParticipant(webinar, this.webinarService.userContact.dbId); } catch { /**/ } }
            }

            this.removeActiveWebinar(webinar);
            this.clearSubscribeToPublisherTimeouts();
            this.ackAllMessages(webinar);
            this.audioMixer.setAudioBalance(0.8);
            webinar.hideRecordInfo = false;
            webinar.lastActorsInfo = undefined;
            this.logger.debug(`[WebinarService] leaveWebinar (web: ${webinar.id}, part: ${localParticipantId}, reason: ${reason}, correlatorId: ${correlatorId}) -- success`);

            return webinar;
        }
        catch (error) {
            this.logger.error(`[WebinarService] leaveWebinar (web: ${webinar.id}, part: ${localParticipantId}, reason: ${reason}, correlatorId: ${correlatorId}) -- ${error.message}`);
            throw error;
        }
    }

    /**
     * Allow to remove the "active" webinar
     * @param webinar - the webinar to remove as active
     */
    public removeActiveWebinar(webinar: Webinar) {
        if (this.activeWebinar && webinar.id === this.activeWebinar.id) {
            this.activeWebinar = null;
            this.activeWebinarSubject.next(RBEvent.create('ON_ACTIVE_WEBINAR', { webinar: null }));
        }
        this.eventService.publish("CHECK_AND_RESET_BUSY_STATE");
    }

    public async addAudioVideoMedia(webinar: Webinar, media: string, originalStream?: MediaStream, replaceBg: boolean = true): Promise<Webinar> {
        const localParticipant = webinar.session.localParticipant;
        const correlatorId = Math.random().toString(36).slice(2, 12);
        try {
            if (!localParticipant) {
                this.logger.error(`[WebinarService] sessionInitiate (web: ${webinar.id}, media: ${media}, correlatorId: ${correlatorId}) -- noLocalParticipant`);
                webinar.rxInfoSubject.next(RBEvent.create('error', 'webinarDisconnected'));
                return webinar;
            }

            if ((media === 'audio' && !localParticipant.medias.audio) || (media === 'video' && !localParticipant.medias.video) || (media === 'audio+video' && !localParticipant.medias.audio && !localParticipant.medias.video)) {
                if (!localParticipant.audioVideoSession) {
                    const medias = (media === 'audio+video') ? ['audio', 'video'] : [media];
                    if (!originalStream) originalStream = await this.webrtcConfigAndUtils.getBrowserMedia(medias, undefined, undefined, undefined, webinar);

                    //this.audioMixerSession?.cleanup();
                    //this.audioMixerSession = await createAudioMixer(originalStream);

                    const stream = replaceBg && this.virtualBackgroundService ? await this.virtualBackgroundService.startProcessStream(originalStream, 'WebinarConferenceService') : originalStream;
                    const sid = `web_sfu_${Math.random().toString(36).slice(2, 12)}`;

                    this.logger.info(`[WebinarService] sessionInitiate (web: ${webinar.id}, type: SFU, media: ${media}, sid: ${sid}, correlatorId: ${correlatorId})`);
                    this.xmppService.connection.jingle.initiateBundleSession(webinar.session.jingleJid, this.xmppService.connection.jid, sid, stream, webinar.roomId);
                    localParticipant.audioVideoSessionId = sid;
                    localParticipant.audioVideoStream = stream;
                    localParticipant.originalAudioVideoStream = originalStream;
                    localParticipant.sendEvent(WebinarSessionParticipantEvents.UPDATE_ORIGINAL_STREAM);
                    //MCOCLEAN localParticipant.sendEvent(WebinarSessionParticipantEvents.UPDATE_MEDIA, { type: media });

                    medias.forEach((amedia: string) => { localParticipant.setMedia(amedia as WebinarSessionParticipantMedia, true); });

                    if (this.webinarExtendedBackstage && (webinar.isOrganizer || webinar.isSpeaker) && this.connectedUser.userType !== UserType.MCUBOT) {
                        const sid = `web_mcu_${Math.random().toString(36).slice(2, 12)}`;
                        localParticipant.mcuAudioSessionId = sid;
                        this.logger.info(`[WebinarService] sessionInitiate (web: ${webinar.id}, type: MCU, media: audio, sid: ${sid})`);
                        const clonedAudioTrack = stream.getAudioTracks()[0]?.clone();
                        if (clonedAudioTrack) {
                            localParticipant.mcuAudioStream = new MediaStream([clonedAudioTrack]);
                            this.xmppService.connection.jingle.initiate(webinar.session.jingleJid, this.xmppService.connection.jid, 'audio', [localParticipant.mcuAudioStream], sid, webinar.roomId, null);
                        }
                    }
                }
                else {
                    // Get new stream
                    let addMedias = [media];
                    if (media === 'audio' && localParticipant.medias.video) { addMedias = ['audio', 'video']; }
                    if (media === 'video' && localParticipant.medias.audio) { addMedias = ['audio', 'video']; }

                    if (!originalStream) originalStream = await this.webrtcConfigAndUtils.getBrowserMedia(addMedias, undefined, undefined, undefined, webinar);
                    if (originalStream) {

                        // Remove existing stream from audioVideoSession
                        let sessionStream = localParticipant.audioVideoSession.localStreams.pop();
                        sessionStream.getTracks().forEach((track: MediaStreamTrack) => { track.enabled = false; track.stop(); track = null; });
                        sessionStream = null;

                        // Rebuild audioVideoSession
                        const stream = replaceBg && this.virtualBackgroundService ? await this.virtualBackgroundService.startProcessStream(originalStream, 'WebinarConferenceService') : originalStream;
                        localParticipant.audioVideoSession.localStreams.push(stream);
                        await localParticipant.audioVideoSession.updateSessionStreamsAndTransceivers(localParticipant.audioVideoSession.localStreams);
                        this.logger.info(`[WebinarService] sessionContentAdd (web: ${webinar.id}, type: SFU, media: ${media}, sid: ${localParticipant.audioVideoSession.sid}, correlatorId: ${correlatorId})`);
                        if (media === 'video') await localParticipant.audioVideoSession.contentAdd('video', true);
                        addMedias.forEach((addMedia: string) => { localParticipant.setMedia(addMedia as WebinarSessionParticipantMedia, true); });
                        localParticipant.audioVideoStream = stream;
                        localParticipant.originalAudioVideoStream = originalStream;
                        localParticipant.sendEvent(WebinarSessionParticipantEvents.UPDATE_ORIGINAL_STREAM);
                        //MCOCLEAN localParticipant.sendEvent(WebinarSessionParticipantEvents.UPDATE_MEDIA, { type: media });
                    }
                }
            }

            if (media === 'sharing' && !localParticipant.medias.sharing) {
                if (!this.webrtcConfigAndUtils.askToAddSharing(`webinar_${webinar.id}`, true)) {
                    await this.getSharingMedia(webinar, true);
                }
            }

            return webinar;
        }
        catch (error) {
            if (error?.message && error?.message?.contains('Permission denied')) {
                this.logger.info(`[WebinarService] sessionInitiate (web: ${webinar.id}, media: ${media}, correlatorId: ${correlatorId}) -- canceled by user`);
                return webinar;
            }
            this.logger.error(`[WebinarService] sessionInitiate (web: ${webinar.id}, media: ${media}, correlatorId: ${correlatorId}) -- failure -- ${error?.message}`);
            webinar.rxInfoSubject.next(RBEvent.create('error', media === 'sharing' ? 'screenSharingNotAvailable' : 'cameraNotAvailable'));
            throw error;
        }
    }

    public async getSharingMedia(webinar: Webinar, shouldGetAudioForSharing: boolean = true): Promise<void> {
        const stream = await this.webrtcConfigAndUtils.getBrowserMedia(['sharing'], undefined, undefined, 24, webinar, shouldGetAudioForSharing);
        await webinar.addSharingMedia(stream);
    }

    public async addSharingMedia(webinar: Webinar, stream: MediaStream): Promise<Webinar> {
        const sid = `web_share_${Math.random().toString(36).slice(2, 12)}`;
        try {
            webinar.session.localParticipant.sharingSession = await this.xmppService.connection.jingle.initiate(webinar.session.jingleJid, this.xmppService.connection.jid, 'sharing', [stream], sid, webinar.roomId, null);
            webinar.session.localParticipant.sharingStream = stream;
            this.logger.info(`[WebinarService] addSharingMedia (web: ${webinar.id}, sid: ${sid}, part: ${webinar.session?.localParticipant?.id})`);
            webinar.session.localParticipant.sharingSessionId = sid;
            webinar.session.localParticipant.setMedia(WebinarSessionParticipantMedia.SHARING, true);
            this.webinarService.submitEvent(WebinarServiceEvents.WEBINAR_SHARED_SCREEN, { webinar, action: 'start' });
            return webinar;
        }
        catch (error) {
            this.logger.error(`[WebinarService] addSharingMedia (web: ${webinar.id}, sid: ${sid}, part: ${webinar.session?.localParticipant?.id}) -- failure -- ${error?.message}`);
            throw error;
        }
    }

    public async removeLocalMedia(webinar: Webinar, media: string): Promise<Webinar> {
        try {
            const localParticipant = webinar.session.localParticipant;

            if ((media === 'audio' && localParticipant.medias.audio) || (media === 'video' && localParticipant.medias.video)) {
                const sid = localParticipant?.audioVideoSession?.sid;

                // Remove existing stream from audioVideoSession
                this.removeAudioVideoMedia(webinar, true);

                // Get new stream
                let addMedias = null;
                if (media === 'audio' && localParticipant.medias.video) { addMedias = ['video']; }
                if (media === 'video' && localParticipant.medias.audio) { addMedias = ['audio']; }
                if (!addMedias) {
                    this.removeAudioVideoMedia(webinar);
                    this.terminateSession(localParticipant, localParticipant.audioVideoSession.sid, 'removeMedia');
                    this.stopStatsForSession(localParticipant.audioVideoSession);
                    localParticipant.audioVideoSession = null;
                    localParticipant.audioVideoStream = undefined;
                    localParticipant.originalAudioVideoStream = null;
                    localParticipant.mcuAudioStream = null;
                    localParticipant.setMedia(media as WebinarSessionParticipantMedia, false);
                    if (!localParticipant.medias.video) { this.virtualBackgroundService?.stopProcessStream(); }
                    this.logger.info(`[WebinarService] sessionContentRemove (web: ${webinar.id}, type: SFU, media: ${media}, sid:${sid})`);
                }

                const originalStream = await this.webrtcConfigAndUtils.getBrowserMedia(addMedias, undefined, undefined, null, webinar);
                const stream = this.virtualBackgroundService ? await this.virtualBackgroundService.startProcessStream(originalStream, 'WebinarConferenceService') : originalStream;

                localParticipant.audioVideoSession.localStreams.push(stream);
                await localParticipant.audioVideoSession.updateSessionStreamsAndTransceivers(localParticipant.audioVideoSession.localStreams);
                await localParticipant.audioVideoSession.contentRemove('video', true);
                this.logger.info(`[WebinarService] sessionContentRemove (web: ${webinar.id}, type: SFU, media: ${media}, sid: ${sid})`);

                // Handle MCU
                if (this.webinarExtendedBackstage && !webinar.isAttendee) {
                    const clonedAudioTrack = stream.getAudioTracks()[0]?.clone();
                    if (clonedAudioTrack) {
                        localParticipant.mcuAudioStream = new MediaStream([clonedAudioTrack]);
                        webinar.session.mcuAudioSession.localStreams.push(localParticipant.mcuAudioStream);
                        webinar.session.mcuAudioSession.updateSessionStreamsAndTransceivers(webinar.session.mcuAudioSession.localStreams);
                    }
                }
                localParticipant.audioVideoStream = stream;
                localParticipant.originalAudioVideoStream = originalStream;
                localParticipant.setMedia(media as WebinarSessionParticipantMedia, false);
                localParticipant.sendEvent(WebinarSessionParticipantEvents.UPDATE_ORIGINAL_STREAM);
                //MCOCLEAN localParticipant.sendEvent(WebinarSessionParticipantEvents.UPDATE_MEDIA, { type: media });
            }

            if (media === 'sharing' && localParticipant.medias.sharing) {
                const sid = localParticipant.sharingSession.sid;
                if (localParticipant.sharingOnStage) await webinar.removeParticipantSharingMediaFromStage(localParticipant);
                this.removeSharingMedia(webinar);
                this.terminateSession(localParticipant, sid, 'removeMedia');
                localParticipant.setMedia(WebinarSessionParticipantMedia.SHARING, false, true);
                this.setBusyState(webinar.session.localParticipant);
                this.webinarService.submitEvent(WebinarServiceEvents.WEBINAR_SHARED_SCREEN, { webinar, action: 'stop' });
                this.logger.info(`[WebinarService] sessionTerminate (web: ${webinar.id}, media: ${media}, sid: ${sid})`);
            }

            return webinar;
        }
        catch (error) {
            this.logger.error(`[WebinarService] removeLocalMedia (web: ${webinar.id}, type: ${media}) -- failure -- ${error.message}`);
            throw error;
        }
    }

    public async muteParticipant(webinar: Webinar, participant: WebinarSessionParticipant, mute: boolean = true): Promise<Webinar> {
        const localParticipant = webinar.session.localParticipant;
        const correlatorId = Math.random().toString(36).slice(2, 12);

        try {
            if (this.webinarExtendedBackstage) {

                // Handle attendee allowed to talk
                if (webinar.isAttendee && participant.id === localParticipant.id && participant.allowedToTalk) {
                    this.logger.info(`[WebinarService] muteParticipant (web: ${webinar.id}, part: ${participant.id}, mute: ${mute}, correlatorId: ${correlatorId})`);
                    const requestId = await this.muteParticipantInternal(webinar, participant, 'SFU', mute);
                    participant.mute = mute;
                    participant.sendEvent(WebinarSessionParticipantEvents.MUTE_PARTICIPANT, mute);
                    this.logger.debug(`[WebinarService] muteParticipant (web: ${webinar.id}, part: ${participant.id}, mute: ${mute}, requestId: ${requestId}, correlatorId: ${correlatorId})`);
                    return webinar;
                }

                let sfuRequestId = '';
                let mcuRequestId = '';
                let muteSFU = true;
                let muteMCU = true;
                let force = false;

                // Handle remote participant
                if (participant.id !== localParticipant.id) { muteSFU = muteMCU = mute; force = true; }
                else {
                    muteSFU = mute || !(participant?.onStage || participant.audioConfig === 'stage');
                    muteMCU = mute ? true : participant?.onStage;
                    force = false;
                }

                this.logger.info(`[WebinarService] muteParticipant (web: ${webinar.id}, part: ${participant.id}, muteSFU: ${muteSFU}, muteMCU: ${muteMCU}, correlatorId: ${correlatorId})`);

                if (force || muteSFU !== participant.muteSFU) sfuRequestId = await this.muteParticipantInternal(webinar, participant, 'SFU', muteSFU);
                if (force || (!webinar.isGuest && muteMCU !== participant.muteMCU)) mcuRequestId = await this.muteParticipantInternal(webinar, participant, 'MCU', muteMCU);

                if (participant.id === localParticipant.id) {
                    localParticipant.mute = mute;
                    localParticipant.muteSFU = muteSFU;
                    localParticipant.muteMCU = muteMCU;
                }
                participant.sendEvent(WebinarSessionParticipantEvents.MUTE_PARTICIPANT, mute);
                this.logger.debug(`[WebinarService] muteParticipant (web: ${webinar.id}, part: ${participant.id}, muteSFU: ${muteSFU}, muteMCU: ${muteMCU}, sfuRequestId: ${sfuRequestId}, mcuRequestId: ${mcuRequestId}, correlatorId: ${correlatorId})`);
            }
            else {
                this.logger.info(`[WebinarService] muteParticipant (web: ${webinar.id}, part: ${participant.id}, mute: ${mute}, correlatorId: ${correlatorId})`);
                const requestId = await this.muteParticipantInternal(webinar, participant, 'SFU', mute);
                if (participant.id === localParticipant.id) participant.mute = mute;
                participant.sendEvent(WebinarSessionParticipantEvents.MUTE_PARTICIPANT, mute);
                this.logger.debug(`[WebinarService] muteParticipant (web: ${webinar.id}, part: ${participant.id}, mute: ${mute}, requestId:${requestId}, correlatorId: ${correlatorId})`);
            }
            return webinar;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, `muteParticipant (web: ${webinar.id}, part: ${participant.id}, mute: ${mute}, correlatorId: ${correlatorId}) `);
            this.logger.error(`[WebinarService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    public async muteParticipantInternal(webinar: Webinar, participant: WebinarSessionParticipant, channel: 'MCU' | 'SFU', mute: boolean = true, askIgnore: boolean = false): Promise<string> {

        const localParticipant = webinar.session.localParticipant;
        let requestId = 'undefined';
        this.logger.debug(`[WebinarService] muteParticipantInternal (web: ${webinar.id}, part: ${participant.id}, channel: ${channel}, mute: ${mute})`);
        if (this.webinarExtendedBackstage || channel === 'SFU') {
            const url = `${config.restServerUrl}/api/rainbow/conference/v1.0/rooms/${webinar.roomId}/users/${participant.id}`;
            const headers = this.authService.getPostHeader();
            let option: any = { option: mute ? 'mute' : 'unmute' };
            if (this.webinarExtendedBackstage && !webinar.isAttendee && this.connectedUser.userType !== UserType.MCUBOT) {
            //if (this.webinarExtendedBackstage && (!webinar.isAttendee || (participant.id === localParticipant.id && localParticipant.allowedToTalk))) {
                if (channel === 'SFU') option = { option: mute ? 'sfuMute' : 'sfuUnmute' };
                if (channel === 'MCU') option = { option: mute ? 'mcuMute' : 'mcuUnmute' };
            }
            const body = JSON.stringify(option);
            const response = await fetch(url, { method: "PUT", headers, body });
            requestId = response.headers.get("x-rainbow-request-id");
            if (!response.ok) throw response;
            const data = (await response.json()).data;
            if (data?.askToUnmute && channel === 'SFU') webinar.rxInfoSubject.next(RBEvent.create('askToUnmute', participant.id));
        }

        if (!this.webinarExtendedBackstage && this.webinarExtendedBackstage && participant.id === localParticipant.id) {
            if (localParticipant?.onStage) this.audioMixer.manageBackstageMic(webinar, false);
            else this.audioMixer.manageBackstageMic(webinar, !mute);
        }
        return requestId;
    }

    public declineUnmuteInvitation(webinar: Webinar, fromParticpant: WebinarSessionParticipant) {
        this.logger.info(`[WebinarService] declineUnmuteInvitation (web: ${webinar.id}, part: ${fromParticpant.id})`);
    }

    public async putOnStage(webinar: Webinar, participantId: string, type: WebinarMediaType, onStage: boolean, master: boolean = false, fullStream: boolean = false): Promise<Webinar> {
        const correlatorId = Math.random().toString(36).slice(2, 12);
        try {
            this.logger.info(`[WebinarService] putOnStage (web: ${webinar.id}, part: ${participantId}, type: ${type}, stage: ${onStage}, master: ${master}, correlatorId: ${correlatorId})`);
            const url = `${config.restServerUrl}/api/rainbow/conference/v1.0/rooms/${webinar.roomId}/users/${participantId}/${onStage ? 'go-on-stage' : 'get-off-stage'}`;
            const headers = this.authService.getPostHeader();
            let body: any = onStage ? { type, master } : { type };
            if (onStage && fullStream) body = { type, fullStream: true };
            const response = await fetch(url, { method: 'PUT', headers, body: JSON.stringify(body) });
            if (!response.ok) throw response;
            const requestId = response.headers.get("x-rainbow-request-id");
            this.logger.debug(`[WebinarService] putOnStage (web: ${webinar.id}, part: ${participantId}, type: ${type}, stage: ${onStage}, master: ${master}, requestId: ${requestId}, correlatorId: ${correlatorId}) -- success`);
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, `putOnStage (web: ${webinar.id}, part: ${participantId}, type: ${type}, stage: ${onStage}, master: ${master}, correlatorId: ${correlatorId})`);
            this.logger.error(`[WebinarService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
        return webinar;
    }

    public async shareAudioConfig(webinar: Webinar, audioConfig: string): Promise<Webinar> {
        const localParticipant = webinar.session.localParticipant;
        try {
            if (webinar.isAttendee) return webinar;
            this.logger.info(`[WebinarService] shareAudioConfig(web: ${webinar.id}, part: ${localParticipant.id}, audioConfig: ${audioConfig})`);
            const url = `${config.restServerUrl}/api/rainbow/conference/v1.0/rooms/${webinar.roomId}/private-communication`;
            const headers = this.authService.getPostHeader();
            const body = JSON.stringify({ listener: audioConfig });
            const response = await fetch(url, { method: 'PUT', headers, body });
            if (!response.ok) throw response;
            const requestId = response.headers.get("x-rainbow-request-id");
            this.logger.debug(`[WebinarService] shareAudioConfig (web: ${webinar.id}, part: ${localParticipant.id}, audioConfig: ${audioConfig}, requestId: ${requestId})`);

        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'shareAudioConfig');
            this.logger.error(`[WebinarService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
        return webinar;
    }

    public async startWebinar(webinar: Webinar): Promise<Webinar> {
        const localParticipant = webinar.session.localParticipant;
        const correlatorId = Math.random().toString(36).slice(2, 12);
        try {
            this.logger.info(`[WebinarService] startWebinar (web: ${webinar.id}, part: ${localParticipant.id}, correlatorId: ${correlatorId}) -- success`);
            const url = `${config.restServerUrl}/api/rainbow/conference/v1.0/rooms/${webinar.roomId}/update`;
            const headers = this.authService.getPostHeader();
            const body = JSON.stringify({ option: 'webinar', recording: webinar.recorded === 'true' });
            const response = await fetch(url, { method: 'PUT', headers, body });
            if (!response.ok) throw response;
            const requestId = response.headers.get("x-rainbow-request-id");
            if (webinar.room) await this.bubbleService.updateRoomFromServer(webinar.room);
            this.logger.info(`[WebinarService] startWebinar (web: ${webinar.id}, part: ${localParticipant.id}, correlatorId: ${correlatorId}, requestId: ${requestId})  -- success`);
            return webinar;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error);
            this.logger.error(`[WebinarService] startWebinar (web: ${webinar.id}, part: ${localParticipant.id}, correlatorId: ${correlatorId}) -- ${errorInfo.error.message}`);
            throw errorInfo.error;
        }
    }

    public async stopWebinar(webinar: Webinar): Promise<Webinar> {
        const localParticipant = webinar.session.localParticipant;
        const correlatorId = Math.random().toString(36).slice(2, 12);
        try {
            this.logger.info(`[WebinarService] stopWebinar (web: ${webinar.id}, part: ${localParticipant.id}, correlatorId: ${correlatorId})`);
            const url = `${config.restServerUrl}/api/rainbow/conference/v1.0/rooms/${webinar.roomId}/stop`;
            const headers = this.authService.getRequestHeader();
            const response = await fetch(url, { method: 'DELETE', headers });
            if (!response.ok) throw response;
            const requestId = response.headers.get("x-rainbow-request-id");
            this.logger.debug(`[WebinarService] stopWebinar (web: ${webinar.id}, part: ${localParticipant.id}, correlatorId: ${correlatorId}, requestId: ${requestId}) -- success`);
            return webinar;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, `stopWebinar (web: ${webinar.id}, part: ${localParticipant.id}, correlatorId: ${correlatorId})`);
            this.logger.error(`[WebinarService] stopWebinar (web: ${webinar.id}, part: ${localParticipant.id}, correlatorId: ${correlatorId}) -- ${errorInfo.error.message}`);
            return webinar;
        }
    }

    public async raiseHand(webinar: Webinar, userId: string): Promise<Webinar> {
        const localParticipant = webinar.session.localParticipant;
        const correlatorId = Math.random().toString(36).slice(2, 12);
        try {
            //const roomId = (webinar.status === WebinarStatus.IN_PROGRESS) ? webinar.roomId : webinar.roomModeratorsChatId;
            this.logger.info(`[WebinarService] raiseHand (web: ${webinar.id}, part: ${localParticipant.id}, correlatorId: ${correlatorId})`);
            const url = `${config.restServerUrl}/api/rainbow/conference/v1.0/rooms/${webinar.roomId}/users/${userId}/raise-hand`;
            const headers = this.authService.getPostHeader();
            const response = await fetch(url, { method: 'PUT', headers });
            if (!response.ok) throw response;
            const requestId = response.headers.get("x-rainbow-request-id");
            webinar.session.localParticipant.raiseHand = true;
            webinar.session.localParticipant.raiseHandDate = new Date();
            webinar.session.localParticipant.rxSubject.next(RBEvent.create('raiseHand', true));
            webinar.rxParticipantsSubject.next(null);
            this.logger.debug(`[WebinarService] raiseHand (web: ${webinar.id}, part: ${localParticipant.id}, correlatorId: ${correlatorId}, requestId: ${requestId})`);
            return webinar;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, "raiseHand");
            this.logger.error(`[WebinarService] raiseHand (web: ${webinar.id}, part: ${localParticipant.id}, correlatorId: ${correlatorId}) -- ${errorInfo.error.message}`);
            throw errorInfo.error;
        }
    }

    public async lowerHand(webinar: Webinar, userId: string): Promise<Webinar> {
        const localParticipant = webinar.session.localParticipant;
        const correlatorId = Math.random().toString(36).slice(2, 12);
        try {
            this.logger.info(`[WebinarService] lowerHand (web: ${webinar.id}, part: ${localParticipant.id}, correlatorId: ${correlatorId})`);
            const url = `${config.restServerUrl}/api/rainbow/conference/v1.0/rooms/${webinar.roomId}/users/${userId}/lower-hand`;
            const headers = this.authService.getPostHeader();
            const response = await fetch(url, { method: 'PUT', headers });
            if (!response.ok) throw response;
            const requestId = response.headers.get("x-rainbow-request-id");
            webinar.session.localParticipant.raiseHand = false;
            webinar.session.localParticipant.raiseHandDate = undefined;
            webinar.session.localParticipant.rxSubject.next(RBEvent.create('raiseHand', false));
            webinar.rxParticipantsSubject.next(null);
            this.logger.debug(`[WebinarService] lowerHand (web: ${webinar.id}, part: ${localParticipant.id}, correlatorId: ${correlatorId}, requestId: ${requestId})`);
            return webinar;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, "lowerHand");
            this.logger.error(`[WebinarService] lowerHand (web: ${webinar.id}, part: ${localParticipant.id}, correlatorId: ${correlatorId}) -- ${errorInfo.error.message}`);
            throw errorInfo.error;
        }
    }

    public async allowAttendeeToTalk(webinar: Webinar, userId: string) {
        const correlatorId = Math.random().toString(36).slice(2, 12);
        try {
            this.logger.info(`[WebinarService] allowAttendeeToTalk (web: ${webinar.id}, part: ${userId}, correlatorId: ${correlatorId})`);
            const url = `${config.restServerUrl}/api/rainbow/conference/v1.0/rooms/${webinar.roomId}/users/${userId}/allow-talk`;
            const headers = this.authService.getPostHeader();
            const response = await fetch(url, { method: 'PUT', headers });
            if (!response.ok) throw response;
            const requestId = response.headers.get("x-rainbow-request-id");
            const participant = webinar.session.getParticipantById(userId);
            participant.raiseHand = false;
            participant.rxSubject.next(RBEvent.create('raiseHand', false));
            webinar.updateRaiseHandParticipants();
            webinar.rxSubject.next(null);
            this.logger.debug(`[WebinarService] allowAttendeeToTalk (web: ${webinar.id}, part: ${userId}, correlatorId: ${correlatorId}, requestId: ${requestId}) -- success`);
            return webinar;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, "allowAttendeeToTalk");
            this.logger.error(`[WebinarService] allowAttendeeToTalk (web: ${webinar.id}, part: ${userId}, correlatorId: ${correlatorId}) -- ${errorInfo.error.message}`);
            throw errorInfo.error;
        }
    }

    public async disableAttendeeToTalk(webinar: Webinar, userId: string): Promise<Webinar> {
        const correlatorId = Math.random().toString(36).slice(2, 12);
        try {
            this.logger.info(`[WebinarService] disableAttendeeToTalk (web: ${webinar.id}, part: ${userId}, correlatorId: ${correlatorId})`);
            const url = `${config.restServerUrl}/api/rainbow/conference/v1.0/rooms/${webinar.roomId}/users/${userId}/disable-talk`;
            const headers = this.authService.getPostHeader();
            const response = await fetch(url, { method: 'PUT', headers });
            if (!response.ok) throw response;
            const requestId = response.headers.get("x-rainbow-request-id");
            this.logger.debug(`[WebinarService] disableAttendeeToTalk (web: ${webinar.id}, part: ${userId}, correlatorId: ${correlatorId}, requestId: ${requestId})`);
            return webinar;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, "disableAttendeeToTalk");
            this.logger.error(`[WebinarService] disableAttendeeToTalk (web: ${webinar.id}, part: ${userId}, correlatorId: ${correlatorId}) -- ${errorInfo.error.message}`);
            throw errorInfo.error;
        }
    }

    public async activateWebinarSound(webinar: Webinar) {
        try {
            this.logger.info(`[WebinarService] activateWebinarSound (web: ${webinar.id})`);
            this.webinarService.browserReady.next(true);
            if (webinar.session) {
                this.logger.info(`[WebinarService] activateWebinarSound (web: ${webinar.id})`);
                this.audioMixer.activateAudio();
            }
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, "activateWebinarSound");
            this.logger.error(`[WebinarService] activateWebinarSound (web: ${webinar.id}) -- failure -- ${errorInfo.error.message}`);
            throw errorInfo.error;
        }
    }

    public async askForStaging(webinar: Webinar, participant: WebinarSessionParticipant, media: string): Promise<void> {
        try {
            this.logger.info(`[WebinarService] askForStaging (web: ${webinar.id}, part: ${participant.id})`);
            const id = "web_" + crypto.randomUUID();
            const message = $msg({ from: this.xmppService.jid, to: participant.contact.jid, type: "set", id });
            message.c('command', { xmlns: 'http://jabber.org/protocol/commands', node: 'request-staging', webinarId: webinar.id, media });
            await this.xmppService.send(message);
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, "askForStaging");
            this.logger.error(`[WebinarService] askForStaging (web: ${webinar.id}, part: ${participant.id}) -- failure -- ${errorInfo.error.message}`);
            throw errorInfo.error;
        }
    }

    public async declineStagingInvitation(webinar: Webinar, participant: WebinarSessionParticipant, media: string): Promise<void> {
        try {
            this.logger.info(`[WebinarService] declineStagingInvitation (web: ${webinar.id}, part: ${participant.id})`);
            const id = "web_" + crypto.randomUUID();
            const message = $msg({ from: this.xmppService.jid, to: participant.contact.jid, type: "set", id });
            message.c('command', { xmlns: 'http://jabber.org/protocol/commands', node: 'refuse-staging', webinarId: webinar.id, media });
            await this.xmppService.send(message);
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, "declineStagingInvitation");
            this.logger.error(`[WebinarService] declineStagingInvitation (web: ${webinar.id}, part: ${participant.id} -- failure -- ${errorInfo.error.message}`);
            throw errorInfo.error;
        }
    }

    public hasActiveWebinarSession(): boolean {
        return this.activeWebinar !== null;
    }

    public isActiveWebinar(webinar: Webinar): boolean {
        return this.activeWebinar && webinar.id === this.activeWebinar.id;
    }

    public async removeAudioVideoMedia(webinar: Webinar, audioVideoOnly: boolean = false): Promise<void> {

        await this.virtualBackgroundService?.stopProcessStream();

        if (webinar.session?.localParticipant?.originalAudioVideoStream) {
            let originalAudioVideoStream = webinar.session.localParticipant.originalAudioVideoStream;
            if (originalAudioVideoStream) {
                originalAudioVideoStream.getTracks().forEach((track: MediaStreamTrack) => { track.enabled = false; track.stop(); track = null; });
                originalAudioVideoStream = undefined;
            }
        }

        if (webinar.session?.localParticipant?.audioVideoSession) {
            const sid = webinar.session?.localParticipant?.audioVideoSession?.sid;
            if (this.settingsService.getSetting('speechEnhancementType') !== "none" || this.settingsService.getSetting("enableSpeechEnhancement") === "true") { this.speechEnhancementService.stopProcessStream("[WebinarService]"); }
            let sessionStream = webinar.session.localParticipant.audioVideoSession.localStreams.pop();
            if (sessionStream) {
                sessionStream.getTracks().forEach((track: MediaStreamTrack) => { track.enabled = false; track.stop(); track = null; });
                sessionStream = null;
            }
            this.logger.info(`[WebinarService] removeAudioVideoMedia (web: ${webinar.id}, type: SFU, sid: ${sid}, part: ${webinar.session?.localParticipant?.id}`);
        }

        if (!audioVideoOnly) {
            if (webinar.session?.localParticipant?.mcuAudioStream) {
                let mcuAudioStream = webinar.session.localParticipant.mcuAudioStream;
                const sid = webinar.session.mcuAudioSession?.sid;
                if (mcuAudioStream) {
                    mcuAudioStream.getTracks().forEach((track: MediaStreamTrack) => { track.enabled = false; track.stop(); track = null; });
                    mcuAudioStream = null;
                    this.logger.info(`[WebinarService] removeAudioVideoMedia (web: ${webinar.id}, type: MCU, sid: ${sid}, part: ${webinar.session?.localParticipant?.id}`);
                }
            }
            if (webinar.session?.mcuAudioSession) webinar.session.mcuAudioSession?.localStreams.pop();
        }

    }

    public removeSharingMedia(webinar: Webinar): void {
        if (webinar.session?.localParticipant?.sharingSession) {
            const sid = webinar.session?.localParticipant?.sharingSession.sid;
            let sessionStream = webinar.session.localParticipant.sharingSession.localStreams.pop();
            if (sessionStream) {
                sessionStream.getTracks().forEach((track: MediaStreamTrack) => { track.enabled = false; track.stop(); track = null; });
                sessionStream = null;
            }
            this.logger.info(`[WebinarService] removeSharingMedia(web: ${webinar.id}, sid: ${sid}, part: ${webinar.session?.localParticipant?.id}`);
        }
    }

    public enableSubtitles(webinar: Webinar, enable: boolean) {
        webinar.subtitlesEnable = enable;
        if (enable) {
            if (webinar.session.localParticipant.audioVideoStream) {
                this.speechRecognitionService.init('webinar', webinar.session.localParticipant.audioVideoStream, webinar.room.jid);
            }
        }
        else {
            this.speechRecognitionService.stop();
        }
    }

    public async applyVideoConstraints(webinar: Webinar, streamType: 'audioVideo' | 'sharing', resolution: WebinarConstraintResolution, frameRate: number, maxBitrate: number): Promise<void> {
        const sid = webinar.session?.localParticipant[`${streamType}Session`]?.sid;
        try {
            const stream = webinar.session?.localParticipant[`${streamType}Stream`];
            const tranceiverIndex = streamType === 'audioVideo' ? 1 : 0;
            const senderTranceiver = webinar.session?.localParticipant[`${streamType}Session`]?.localTransceivers[tranceiverIndex].sender;

            if (stream && senderTranceiver) {
                const track = stream.getVideoTracks()[0];

                // Compute scaling informations
                const { scaleFactor, scaleInfo } = this.computeScaleFactor(track, resolution, streamType === 'sharing');

                // Compute maxBitRate
                const appliedMaxBitrate = localStorage.getItem('webinarConstantMaxBitRate') === 'true' ? (streamType === 'sharing' ? 1500000 : 1200000) : maxBitrate * 1000;
                const maxBitrateInfo = `${appliedMaxBitrate / 1000}k`;

                // Apply scale factor and maxBitRate
                const params = senderTranceiver.getParameters();
                params.encodings[0].scaleResolutionDownBy = scaleFactor;
                params.encodings[0].maxBitrate = appliedMaxBitrate;
                senderTranceiver.setParameters(params);

                // Apply frame rate constraints on src track
                if (streamType === 'audioVideo') {
                    const originalStream = webinar.session?.localParticipant.originalAudioVideoStream;
                    const originalTrack = originalStream.getVideoTracks()[0];
                    const constraints = originalTrack.getConstraints();
                    constraints.frameRate = frameRate;
                    await originalTrack.applyConstraints(constraints);
                    this.virtualBackgroundService?.setFramerate(frameRate);
                }
                else {
                    const constraints = track.getConstraints();
                    constraints.frameRate = frameRate;
                    await track.applyConstraints(constraints);
                }

                this.logger.debug(`[WebinarService] applyVideoConstraints (web: ${webinar.id}, media: ${streamType}, sid: ${sid}, ${scaleInfo}, frameRate: ${frameRate}fps, maxBitRate: ${maxBitrateInfo})`);
            }
        }
        catch (error) { this.logger.error(`[WebinarService] applyVideoConstraints (web: ${webinar.id}, media: ${streamType}, sid: ${sid})  -- failure -- ${error.message}`); }
    }

    private async resendStageEvents(webinar: Webinar): Promise<void> {
        const correlatorId = Math.random().toString(36).slice(2, 12);
        try {
            if (!webinar.lastActorsInfo) return null;
            this.logger.infoEvent(`[WebinarService] onStageUpdate (web: ${webinar.id}, simulated: true, correlatorId: ${correlatorId})`);
            await this.updateStage(webinar, webinar.lastActorsInfo);
            this.logger.debugEvent(`[WebinarService] onStageUpdate (web: ${webinar.id}, simulated: true, correlatorId: ${correlatorId}) -- success`);
        }
        catch (error) { this.logger.error(`[WebinarService] handleStage (web: ${webinar.id}, simulated: true, correlatorId: ${correlatorId}) --  ${error.message}`); }
    }

    private isParticipantAnActor(webinar: Webinar, participant: WebinarSessionParticipant): boolean {
        if (!webinar.lastActorsInfo) return false;
        return webinar.lastActorsInfo['audioVideo'][participant.id] || webinar.lastActorsInfo['sharing'][participant.id];
    }

    private computeScaleFactor(track: MediaStreamTrack, resolution: WebinarConstraintResolution, sharing: boolean): any {
        const trackInfo = track.getSettings();
        const width = trackInfo.width;
        const height = trackInfo.height;
        const aspectRatio = trackInfo.aspectRatio;

        let ratio: string;
        switch (aspectRatio) {
            case 16 / 9: ratio = '(16/9)'; break;
            case 16 / 10: ratio = '(16/10)'; break;
            case 4 / 3: ratio = '(4/3)'; break;
            case 5 / 4: ratio = '(5/4)'; break;
            default: ratio = '(unknown)'; break;
        }

        let expectedHeight: number;
        switch (resolution) {
            case 'high': expectedHeight = sharing ? 1080 : 720; break;
            case 'medium': expectedHeight = 360; break;
            case 'low': expectedHeight = 180; break;
            case 'min': expectedHeight = 110; break;
            default: expectedHeight = 360; break;
        }

        const scaleFactor = expectedHeight < height ? height / expectedHeight : 1;
        const srcInfo = `${width}x${height}${ratio}`;
        const resInfo = `${resolution}(${expectedHeight}p)`;
        const destInfo = `${Math.floor(width / scaleFactor)}x${Math.floor(height / scaleFactor)}${ratio}`;
        const scaleInfo = `srcRes:${srcInfo} => expRes "${resInfo}" => scaleFactor:${scaleFactor.toFixed(2)} => compRes:${destInfo}`;
        return { scaleFactor, scaleInfo };
    }

    public disableIncomingStreams(webinar: Webinar, media: 'video' | 'sharing', disabled: boolean): Webinar {
        webinar.incomingStreamsDisabled[media] = disabled;
        webinar.session.speakerParticipants.forEach((participant) => {
            this.pauseVideoStream(webinar, participant, media, disabled, 'global');
        });
        return webinar;
    }

    public async pauseVideoStream(webinar: Webinar, participant: WebinarSessionParticipant, media: 'video' | 'sharing', paused: boolean, origin: string): Promise<Webinar> {
        // Never try to pause localParticipant video
        if (participant.id === webinar.session.localParticipant.id) return webinar;

        const session: JingleSession = participant[media === 'video' ? 'audioVideoSession' : 'sharingSession'];
        if (!session) return webinar;
        const videoStreamAvailable = participant.medias[media === 'video' ? 'video' : 'sharing'];
        if (!videoStreamAvailable) return webinar;
        if (paused) {
            if (!session.onHold) {
                await session.sendHold(true);
                participant.sendEvent(WebinarSessionParticipantEvents.UPDATE_MEDIA, { type: media });
                this.logger.info(`[WebinarService] pauseVideoStream (web: ${webinar.id}, part: ${participant.id}, media: ${media}, paused: ${paused}, origin: ${origin}})`);
            }
        }
        else {
            if (session.onHold && !webinar.incomingStreamsDisabled[media]) {
                await session.sendHold(false);
                participant.sendEvent(WebinarSessionParticipantEvents.UPDATE_MEDIA, { type: media });
                this.logger.info(`[WebinarService] pauseVideoStream (web: ${webinar.id}, part: ${participant.id}, media: ${media}, paused: ${paused}, origin: ${origin}})`);
            }
        }
        return webinar;
    }

    public async waitForGuestXmppReconnection(): Promise<void> {
        return this.xmppService.waitForGuestXmppReconnection();
    }

    //****************************************************************//
    //** PRIVATE METHODS                                            **//
    //****************************************************************//
    private startConference(webinar: Webinar): Promise<Webinar> {
        const localParticipantId = this.connectedUser.dbId;
        const correlatorId = Math.random().toString(36).slice(2, 12);
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        return new Promise<Webinar>(async (resolve: any, reject: any) => {
            if (webinar.session) resolve(webinar);
            try {
                if (webinar.isAttendee) {
                    webinar.session = WebinarSession.create();
                    this.logger.info(`[WebinarService] startConference (web: ${webinar.id}, part: ${localParticipantId}, correlatorId: ${correlatorId}) -- attendee create a session object`);
                    resolve(webinar);
                }
                else {
                    const startTimeout = setTimeout(() => {
                        if (this.startConferencePromiseResolvers[webinar.id]) {
                            this.startConferencePromiseResolvers[webinar.id] = null;
                            delete this.startConferencePromiseResolvers[webinar.id];
                        }
                        this.logger.info(`[WebinarService] startConference (web: ${webinar.id}, part: ${localParticipantId}, correlatorId: ${correlatorId}) -- start session timeout`);
                        reject(new Error('Webinar startConference timeout'));
                    }, 8000);

                    this.startConferencePromiseResolvers[webinar.id] = () => {
                        clearTimeout(startTimeout);
                        if (this.startConferencePromiseResolvers[webinar.id]) { delete this.startConferencePromiseResolvers[webinar.id]; }
                        resolve(webinar);
                    };

                    this.logger.info(`[WebinarService] startConference (web: ${webinar.id}, part: ${localParticipantId}, correlatorId: ${correlatorId})`);
                    const url = `${config.restServerUrl}/api/rainbow/conference/v1.0/rooms/${webinar.roomId}/start`;
                    const headers = this.authService.getPostHeader();
                    const response = await fetch(url, { method: "POST", headers });
                    if (!response.ok) {
                        const clone = response.clone();
                        const data = await clone.json();
                        // For unknown reason, the webinar room is inactive, send initial presence to reactivate it !
                        if (data.errorDetailsCode === 409111 && webinar.room) {
                            this.logger.info(`[WebinarService] startConference (web: ${webinar.id}, part: ${localParticipantId}, correlatorId: ${correlatorId}) -- webinar room is inactive, send initial presence to reactivate it`);
                            await this.bubbleService.sendInitialRoomPresenceSync(webinar.room as BubbleRB);
                            const response = await fetch(url, { method: "POST", headers });
                            if (!response.ok) throw response;
                            const requestId = response.headers.get("x-rainbow-request-id");
                            this.logger.debug(`[WebinarService] startConference (web: ${webinar.id}, part: ${localParticipantId}, requestId: ${requestId}, correlatorId: ${correlatorId}) -- success`);
                        }
                        else throw response;
                    }
                    else {
                        const requestId = response.headers.get("x-rainbow-request-id");
                        this.logger.debug(`[WebinarService] startConference (web: ${webinar.id}, part: ${localParticipantId}, requestId: ${requestId}, correlatorId: ${correlatorId}) -- success`);
                    }
                }
            }
            catch (error) {
                const errorInfo = await this.errorHelperService.getErrorInfo(error);
                this.logger.error(`[WebinarService] startConference (web: ${webinar.id}, part: ${localParticipantId}, correlatorId: ${correlatorId}) -- ${errorInfo.message}`);
                reject(errorInfo.error as Error);
            }
        });
    }

    /**
     * Joins an existing webinar conference session.
     *
     * This method handles the process of joining a webinar conference by:
     * - Sending a POST request to the conference join endpoint
     * - Processing stage information for attendees
     * - Handling switch device scenarios when user is already connected
     *
     * @param webinar - The webinar instance to join
     * @returns A promise that resolves when the user has successfully joined the conference
     * @throws Error with message 'alreadyConnected' if the user is already connected from another device
     * @throws Error from errorHelperService if the join request fails
     */
    private async joinConference(webinar: Webinar): Promise<void> {
        const localParticipantId = this.connectedUser.dbId;
        const correlatorId = Math.random().toString(36).slice(2, 12);
        try {
            this.logger.info(`[WebinarService] joinConference (web: ${webinar.id}, part: ${localParticipantId}, correlatorId: ${correlatorId})`);
            this.subscribeToPublisherPromiseResolvers = {};

            // Handle audio/video codecs
            const codecs = this.manageCodecs();
            if (this.connectedUser.userType === UserType.MCUBOT) codecs.video = "h264";

            // Send POST request to the conference join endpoint
            const url = `${config.restServerUrl}/api/rainbow/conference/v1.0/rooms/${webinar.roomId}/join?context=webinar`;
            const headers = this.authService.getPostHeader();
            const resourceId = this.xmppService.getResourceFromJid(this.xmppService.fullJid);
            let params: any = { resourceId, codecs, mute: false };
            if (this.webinarExtendedBackstage) {
                if (this.connectedUser.userType === UserType.MCUBOT) params = { resourceId, codecs };
                else if (!webinar.isAttendee) params = { resourceId, codecs, sfuMute: true, mcuMute: true };
            }
            if (this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.UNMUTE_REQUEST)) { params['askToUnmute'] = true; }
            const body = JSON.stringify(params);
            const response = await fetch(url, { method: 'POST', headers, body });
            const requestId = response.headers.get("x-rainbow-request-id");
            if (!response.ok) throw response;
            const data = (await response.json()).data;
            const message = data.isAlreadyConnected === true ? '-- switchDevice detected' : '';
            this.logger.debug(`[WebinarService] joinConference (web: ${webinar.id}, part: ${localParticipantId}, requestId: ${requestId}, correlatorId: ${correlatorId}, videoCodec: ${codecs.video}) ${message} -- success`);

            if (webinar.session) webinar.session.jingleJid = data.jingleJid;

            // Process stage information for attendee
            // Stage info event is sometime received to early, then we use the stage info contains in the 200 response...
            const actors = data.stage?.actors;
            if (actors?.length) {
                const actorsInfo: any = { audioVideo: {}, sharing: {}, mix: {} };
                actors.forEach((actor: any) => {
                    const type = actor.type === 'participant' ? 'audioVideo' : actor.type;
                    const state = actor.master ? 'master' : 'staged';
                    const participantId = actor.userId;
                    actorsInfo[type][participantId] = state;
                });

                // !!!!!!! TEMPORAIRE: REMOVE MIX BOT FROM PARTICIPANTS ARRAY !!!!!!
                const mixBotId = Object.keys(actorsInfo['mix'])[0];
                if (mixBotId) delete actorsInfo['audioVideo'][mixBotId];

                webinar.lastActorsInfo = actorsInfo;
                if (webinar.isAttendee) await this.updateStage(webinar, actorsInfo);
            }

            if (data.isAlreadyConnected === true) throw new Error('alreadyConnected');
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, `joinConference (web: ${webinar.id}, part: ${localParticipantId}, correlatorId: ${correlatorId})`);
            throw errorInfo.error;
        }
    }

    private manageCodecs(): WebinarCodecs {
        const codecs: WebinarCodecs = {};
        const defaultCodec = localStorage.getItem('enableWebinarSimulcast') !== 'false' ? 'vp8' : 'vp8';
        const storedVideoCodec = localStorage.getItem('webinarVideoCodec') || defaultCodec;
        const storedSharingCodec = localStorage.getItem('webinarSharingCodec') || "h264";
        if (storedVideoCodec) codecs.video = storedVideoCodec;
        if (storedSharingCodec) codecs.sharing = storedSharingCodec;
        return codecs;
    }

    private async stopSession(webinar: Webinar): Promise<Webinar> {
        try {
            webinar.action = 'stopSession';
            const url = `${config.restServerUrl}/api/rainbow/conference/v1.0/rooms/${webinar.roomId}/stop`;
            const headers = this.authService.getPostHeader();
            const body = JSON.stringify({ webinarRoomType: 'practiceRoom' });
            const response = await fetch(url, { method: "DELETE", headers, body });
            if (!response.ok) throw response;
            this.logger.info(`[WebinarService] stopSession (web: ${webinar.id}) -- success`);
            return webinar;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, "stopSession");
            throw errorInfo.error;
        }
    }

    private async disconnectParticipant(webinar: Webinar, participantId: string): Promise<Webinar> {
        try {
            let url = `${config.restServerUrl}/api/rainbow/conference/v1.0/rooms/${webinar.room.dbId}/users/${participantId}`;
            if (this.bubbleService.context === 'webinar') url += '?context=webinar';
            const headers = this.authService.getRequestHeader();
            const response = await fetch(url, { method: "DELETE", headers });
            if (!response.ok) throw response;

            if (webinar.session) {
                if (participantId === this.connectedUser.dbId) {
                    this.clearSession(webinar);
                    webinar.session.localParticipant = undefined;
                    webinar.session.speakerParticipants = [];
                }
                else webinar.session.removeParticipant(participantId);
            }
            webinar.rxParticipantsSubject.next(null);

            this.logger.info(`[WebinarService] disconnectParticipant (web: ${webinar.id}, part: ${participantId})`);
            return webinar;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, "disconnectParticipant");
            throw errorInfo.error;
        }
    }

    //****************************************************************//
    //** EVENT LISTENERS                                            **//
    //****************************************************************//
    private async onGroupchatMessage(stanza: any) {
        try {
            const stanzaElem = $(stanza);
            const eventElem = stanzaElem.find("event[xmlns='jabber:iq:notification']");
            if (eventElem.length) {
                const roomBareJid = this.xmppService.getBareJidFromJid(stanzaElem.attr('from'));
                const bubble = this.bubbleService.getBubbleFromCache(roomBareJid) as BubbleRB;
                if (!bubble) return true;

                const webinar: Webinar = bubble?.isWebinarBubble() ? await this.webinarService.getWebinar(bubble.conference.webinar.id) : null;
                if (!webinar) throw new Error(`webinar not found for roomId : ${bubble.dbId}`);

                switch (eventElem.attr('name')) {

                    case 'conferenceAdd': {
                        const webinarTypeElem = stanzaElem.find("webinar").find("room-type");
                        const reason = webinarTypeElem.length ? webinarTypeElem.text() : null;
                        if (!webinar.isAttendee && webinarTypeElem.length) {
                            if (reason === 'waitingRoom') {
                                webinar.status = WebinarStatus.WAITING_ROOM;
                                webinar.sendEvent(WebinarEvents.ON_STATUS_CHANGE, WebinarStatus);
                                return true;
                            }
                        }

                        if (!webinar.session) {
                            const from = eventElem.attr('jid');
                            let userId = (await this.contactService.getContactByJid(from))?.dbId;
                            userId = userId ? userId : from;

                            // Connected user but no resolver (Timeout fired on create session method)
                            if (!this.startConferencePromiseResolvers[webinar.id] && this.xmppService.isConnectedJid(from)) {
                                webinar.session = WebinarSession.create();
                                this.logger.infoEvent(`[WebinarService] onConferenceAdd (web: ${webinar.id}, reason: ${reason}, from: ${userId}) -- ignored (timeout)`);
                            }
                            else {
                                this.logger.infoEvent(`[WebinarService] onConferenceAdd (web: ${webinar.id}, reason: ${reason}, from: ${userId})`, { type: 'stanza', data: stanza });
                                webinar.session = WebinarSession.create();
                                if (this.startConferencePromiseResolvers[webinar.id]) { this.startConferencePromiseResolvers[webinar.id](); }
                                webinar.rxSubject.next(null);
                                this.webinarService.submitEvent(WebinarServiceEvents.WEBINARS_UPDATE);
                                this.webinarService.submitEvent(WebinarServiceEvents.WEBINAR_NOTIFICATION, { webinar, from, action: 'start' });
                            }
                        }
                    } break;

                    case 'conferenceRemove': {
                        const correlatorId = Math.random().toString(36).slice(2, 12);
                        this.logger.infoEvent(`[WebinarService] onConferenceRemove (web: ${webinar.id}, correlatorId: ${correlatorId})`);

                        const webinarTypeElem = stanzaElem.find("webinar").find("room-type");
                        if (webinarTypeElem.length) {
                            const reason = webinarTypeElem.text();
                            if (reason === 'webinar' || reason === 'waitingRoom') webinar.status = WebinarStatus.TERMINATED;
                            if (reason === 'practiceRoom') {
                                webinar.status = webinar.isWaitingRoomStarted() ? WebinarStatus.WAITING_ROOM : WebinarStatus.UPCOMING;
                                const bubble = webinar.room as BubbleRB;
                                const practiceBubble = webinar.practiceRoom as BubbleRB;
                                if (bubble?.conference?.sessions) bubble.conference.sessions = [];
                                if (practiceBubble?.conference?.sessions) practiceBubble.conference.sessions = [];
                                const from = eventElem.attr('jid');
                                this.webinarService.submitEvent(WebinarServiceEvents.WEBINAR_NOTIFICATION, { webinar, from, action: 'stop' });
                            }
                        }

                        // Force release media-devices
                        if (webinar.session?.localParticipant) this.removeAudioVideoMedia(webinar);

                        webinar.session = null;
                        webinar.rxSubject.next(null);
                        this.webinarService.submitEvent(WebinarServiceEvents.WEBINARS_UPDATE);
                        if (webinar.status === WebinarStatus.TERMINATED) {
                            this.ackAllMessages(webinar);
                            this.webinarService.submitEvent(WebinarServiceEvents.WEBINAR_TERMINATED, webinar);
                        }
                        this.logger.debugEvent(`[WebinarService] onConferenceRemove (web: ${webinar.id}, correlatorId: ${correlatorId})`);
                    } break;

                    case 'webinarStart':
                        const correlatorId = Math.random().toString(36).slice(2, 12);
                        // Receive start message but not already in room, then notify attendee
                        if (webinar.isAttendee && !webinar.room) { this.webinarService.submitEvent(WebinarServiceEvents.WEBINAR_NOTIFICATION, { webinar, action: 'start' }); }
                        const bubble = webinar.room as BubbleRB;
                        if (bubble?.conference?.sessions.length && bubble?.conference?.sessions[0].startDate) { bubble.conference.sessions[0].startDate = new Date(); }
                        this.logger.infoEvent(`[WebinarService] onWebinarStart (web: ${webinar.id}, correlatorId: ${correlatorId})`);

                        await this.handleStage(webinar, stanzaElem);
                        this.handleRecording(webinar, stanzaElem);

                        webinar.status = WebinarStatus.IN_PROGRESS;
                        webinar.rxSubject.next(null);

                        this.webinarService.submitEvent(WebinarServiceEvents.WEBINARS_UPDATE);
                        this.webinarService.submitEvent(WebinarServiceEvents.WEBINAR_NOTIFICATION, { webinar, from: eventElem.attr('jid'), action: 'start' });
                        this.logger.debugEvent(`[WebinarService] onWebinarStart (web: ${webinar.id}, correlatorId: ${correlatorId})`);
                        break;

                    case 'stageUpdate':
                        const correlatorId2 = Math.random().toString(36).slice(2, 12);
                        if (webinar.session?.localParticipant) {
                            this.logger.infoEvent(`[WebinarService] onStageUpdate (web: ${webinar.id}, correlatorId: ${correlatorId2})`, { type: 'stanza', data: stanza });
                            await this.handleStage(webinar, stanzaElem);
                            this.logger.debugEvent(`[WebinarService] onStageUpdate (web: ${webinar.id}, correlatorId: ${correlatorId2}) -- success`);
                        }
                        break;

                    case 'webinarDelete':
                        this.webinarService.removeWebinar(webinar);
                        this.logger.infoEvent(`[WebinarService] onWebinarDelete (web: ${webinar.id})`);
                        break;

                    default: break;
                }
            }

            return true;
        }
        catch (error) {
            this.logger.error(`[WebinarService] onGroupchatMessage -- ${error.message}`);
            return true;
        }
    }

    private async ackAllMessages(webinar: Webinar): Promise<void> {
        if (webinar.room) {
            let conversation = this.webinarService.conversationService.getConversationFromCache(webinar.room.jid);
            if (!conversation) { conversation = await this.webinarService.conversationService.getOrCreateConversation(webinar.room.jid, 'room'); }
            this.webinarService.conversationService.ackAllMessages(conversation);
        }
        if (webinar.practiceRoom) {
            let conversation = this.webinarService.conversationService.getConversationFromCache(webinar.practiceRoom.jid);
            if (!conversation) { conversation = await this.webinarService.conversationService.getOrCreateConversation(webinar.practiceRoom.jid, 'room'); }
            this.webinarService.conversationService.ackAllMessages(conversation);
        }
    }

    private async onXmppJingleMessage(stanza: any) {
        try {
            const stanzaElem = $(stanza);
            const fromJid = stanzaElem.attr("from");
            const proposeElem = stanzaElem.find("propose");
            if (proposeElem.length > 0) {
                const confId = proposeElem.find("conference").attr("id");
                const webinar = this.webinarService.getWebinarBySessionId(null, confId);
                if (webinar) {
                    const id = proposeElem.attr("id");
                    let messageId = "web_" + crypto.randomUUID();
                    this.xmppService.send($msg({ from: this.connectedUser.fullJid, to: this.connectedUser.jid, id: messageId })
                        .c('accept', { xmlns: 'urn:xmpp:jingle-message:0', id }));

                    messageId += "1";
                    this.xmppService.send($msg({ from: this.connectedUser.fullJid, to: this.xmppService.getBareJidFromJid(fromJid), id: messageId })
                        .c('proceed', { xmlns: 'urn:xmpp:jingle-message:0', id }));

                    this.logger.infoEvent(`[WebinarService] onXmppJingleMessage(web: ${webinar.id}) -- accept propose and proceed`);
                }
            }
            return true;
        }
        catch (error) {
            this.logger.error(`[WebinarService] onXmppJingleMessage -- ${error.message}`);
            return true;
        }
    }

    private async onAskStagingMessage(stanza: any) {
        try {
            const stanzaElem = $(stanza);
            const from = stanzaElem.attr('from');
            const commandElem = stanzaElem.find('command');
            const webinarId = commandElem.attr('webinarId');
            const media = commandElem.attr('media');
            //ignore other "command" messages received that are not related to the webinar
            if (!webinarId) { return true; }
            const fromContact = this.contactService.getContactFromCacheByJid(this.xmppService.getBareJidFromJid(from));
            const webinar = await this.webinarService.getWebinar(webinarId);

            if (commandElem.length > 0) {
                const command = commandElem.attr('node');
                this.logger.info(`[WebinarService] onAskStagingMessage (web: ${webinarId}, part: ${fromContact.dbId}, command: ${command})`);

                switch (command) {
                    case 'request-staging':
                        this.webinarService.submitEvent(WebinarServiceEvents.WEBINAR_REQUEST_STAGING, { webinar, fromContact, media });
                        break;
                    case 'refuse-staging':
                        this.webinarService.submitEvent(WebinarServiceEvents.WEBINAR_REFUSE_STAGING, { webinar, fromContact, media });
                        break;
                    default: break;
                }
            }
            return true;
        }
        catch (error) {
            this.logger.error(`[WebinarService] onAskStagingMessage() -- ${error.message}`);
            return true;
        }
    }

    private async onConferenceMessage(stanza: any): Promise<any> {
        let internalStep: string = 'enter';
        try {
            const stanzaElem = $(stanza);
            const roomId = stanzaElem.find('room-id').text();
            const webinar = this.webinarService.getWebinarByRoomId(roomId);
            if (!webinar) { /*this.logger.debug(`[WebinarService] onConferenceMessage(roomId: ${roomId}) -- no webinar`);*/ return true; }

            // Handle conference-info
            const conferenceInfoElems = stanzaElem.find('conference-info');
            if (conferenceInfoElems.length) {

                // Handle event
                const event = $(conferenceInfoElems).attr('event');
                switch (event) {
                    case 'webinarStart':
                        internalStep = 'event webinarStart';
                        // Receive start message but not already in room, then notify attendee
                        if (webinar.isAttendee && !webinar.room) { this.webinarService.submitEvent(WebinarServiceEvents.WEBINAR_NOTIFICATION, { webinar, action: 'start' }); }
                        const webinarBubble = webinar.room as BubbleRB;
                        if (webinarBubble?.conference?.sessions.length && webinarBubble.conference.sessions[0].startDate) { webinarBubble.conference.sessions[0].startDate = new Date(); }
                        webinar.status = WebinarStatus.IN_PROGRESS;
                        webinar.rxSubject.next(null);
                        this.handleRecording(webinar, stanzaElem);
                        this.webinarService.submitEvent(WebinarServiceEvents.WEBINARS_UPDATE);
                        this.logger.infoEvent(`[WebinarService] onWebinarStart (web: ${webinar.id})`);
                        return true;

                    case 'webinarDelete':
                        internalStep = 'event webinarDelete';
                        this.webinarService.removeWebinar(webinar);
                        this.logger.infoEvent(`[WebinarService] onWebinarDelete (web: ${webinar.id})`);
                        break;

                    case 'conferenceAdd':
                        internalStep = 'event conferenceAdd';
                        if (!webinar.session) {
                            const webinarTypeElem = stanzaElem.find("webinar").find("room-type");
                            if (webinarTypeElem.length) {
                                const reason = webinarTypeElem.text();
                                webinar.session = WebinarSession.create();
                                this.logger.infoEvent(`[WebinarService] onConferenceAdd (web:${webinar.id}, reason: ${reason})`);
                                if (this.startConferencePromiseResolvers[webinar.id]) { this.startConferencePromiseResolvers[webinar.id](); }
                                webinar.rxSubject.next(null);
                                this.webinarService.submitEvent(WebinarServiceEvents.WEBINARS_UPDATE);
                                this.webinarService.submitEvent(WebinarServiceEvents.WEBINAR_NOTIFICATION, { webinar, action: 'startWaitingRoom' });
                            }
                        }
                        break;

                    case 'conferenceRemove':
                        internalStep = 'event conferenceRemove';
                        const webinarTypeElem = stanzaElem.find("webinar").find("room-type");
                        if (webinarTypeElem.length) {
                            const reason = webinarTypeElem.text();
                            if (reason === 'webinar' || reason === 'waitingRoom') webinar.status = WebinarStatus.TERMINATED;
                            if (reason === 'practiceRoom') {
                                webinar.status = WebinarStatus.UPCOMING;
                                (webinar.practiceRoom as BubbleRB).conference.sessions = [];
                            }
                        }

                        // Force release media-devices
                        if (webinar.session?.localParticipant) {
                            this.logger.error(`[WebinarService] onConferenceRemove (web:${webinar.id}) -- force release media-devices`);
                            this.removeAudioVideoMedia(webinar);
                        }

                        webinar.session = null;
                        webinar.rxSubject.next(null);
                        this.webinarService.submitEvent(WebinarServiceEvents.WEBINARS_UPDATE);
                        if (webinar.status === WebinarStatus.TERMINATED) this.webinarService.submitEvent(WebinarServiceEvents.WEBINAR_TERMINATED, webinar);

                        this.logger.infoEvent(`[WebinarService] onConferenceRemove (web: ${webinar.id}) -- session terminate`);
                        return true;

                    default: break;
                }

                // Handle conference-state
                const conferenceStateElems = stanzaElem.find('conference-state');
                if (conferenceStateElems.length) {
                    internalStep = 'conference-state';
                    const activeConference = $(conferenceStateElems).find('active').text() === 'true';
                    if (!activeConference) {
                        this.removeActiveWebinar(webinar);

                        // Force release media-devices
                        if (webinar.session?.localParticipant) {
                            this.logger.error(`[WebinarService] onConferenceRemove (web: ${webinar.id}) -- force release media-devices`);
                            this.removeAudioVideoMedia(webinar);
                        }

                        webinar.session = null;
                        if (webinar.status === WebinarStatus.IN_PROGRESS) {
                            webinar.status = WebinarStatus.TERMINATED;
                            webinar.rxSubject.next(null);
                            this.webinarService.submitEvent(WebinarServiceEvents.WEBINARS_UPDATE);
                        }
                        this.logger.infoEvent(`[WebinarService] onSessionTerminated (web: ${webinar.id})`);
                        return true;
                    }
                }

                // Handle action
                const action = $(conferenceInfoElems[0]).attr('action');

                if (webinar.session) {
                    if (action === 'raise-hand' || action === 'lower-hand') {
                        internalStep = 'action raise-hand or lower-hand';
                        const participantId = stanzaElem.find('user-id').text();
                        const participant = webinar.session.getParticipantById(participantId);
                        if (participant) {
                            this.logger.infoEvent(`[WebinarService] on${action === 'raise-hand' ? 'Raise' : 'Lower'}Hand (web: ${webinar.id}, part: ${participantId})`, { type: 'stanza', data: stanza });
                            participant.raiseHand = action === 'raise-hand';
                            participant.raiseHandDate = participant.raiseHand ? new Date() : undefined;
                            participant.rxSubject.next(RBEvent.create('raiseHand', participant.raiseHand));
                            webinar.rxInfoSubject.next(RBEvent.create('raiseHand', participant));
                            webinar.updateRaiseHandParticipants();
                            webinar.rxParticipantsSubject.next(null);
                            webinar.rxSubject.next(null);
                        }
                    }

                    if (action === 'sfu-mute') {
                        const participantId = stanzaElem.find('user-id').text();
                        const participant = webinar.session.getParticipantById(participantId);
                        if (participant) {
                            internalStep = 'sfu-mute';
                            this.logger.infoEvent(`[WebinarService] onSFUMute (web: ${webinar.id}, part: ${participantId})`, { stanza });
                            participant.muteSFU = true;
                            participant.mute = participant.muteSFU && participant.muteMCU;
                        }
                    }

                    if (action === 'mcu-mute') {
                        const participantId = stanzaElem.find('user-id').text();
                        const participant = webinar.session.getParticipantById(participantId);
                        if (participant) {
                            internalStep = 'mcu-mute';
                            this.logger.infoEvent(`[WebinarService] onSFUMute (web: ${webinar.id}, part: ${participantId})`, { stanza });
                            participant.muteMCU = true;
                            participant.mute = participant.muteSFU && participant.muteMCU;
                            participant.sendEvent(WebinarSessionParticipantEvents.UPDATE_PARTICIPANT, 'mute');
                        }
                    }

                    if (action === 'mute') {
                        const participantId = stanzaElem.find('user-id').text();
                        const participant = webinar.session.getParticipantById(participantId);
                        if (participant) {
                            internalStep = 'mute';
                            this.logger.infoEvent(`[WebinarService] onMute (web: ${webinar.id}, part: ${participantId})`, { type: 'stanza', data: stanza });
                            participant.mute = participant.muteSFU = participant.muteMCU = true;
                            participant.sendEvent(WebinarSessionParticipantEvents.UPDATE_PARTICIPANT, 'mute');
                        }
                    }

                    if (action === 'unmute') {
                        internalStep = 'unmute';
                        const participantId = stanzaElem.find('user-id').text();
                        const participant = webinar.session.getParticipantById(participantId);
                        if (participant && !participant.isLocalParticipant()) {
                            participant.mute = false;
                            this.logger.infoEvent(`[WebinarService] onUnmute (web: ${webinar.id}, part: ${participantId})`, { type: 'stanza', data: stanza });
                            participant.sendEvent(WebinarSessionParticipantEvents.UPDATE_PARTICIPANT, 'unmute');
                        }

                        const askedById = $(conferenceInfoElems[0]).find('asked-by').text();
                        if (askedById !== '') {
                            this.logger.infoEvent(`[WebinarService] onUnmute (web: ${webinar.id}, part: ${webinar.session?.localParticipant.id}, askBy: ${askedById})`, { type: 'stanza', data: stanza });
                            const askedBy = await this.contactService.getContactByDBId(askedById);
                            this.webinarService.submitEvent(WebinarServiceEvents.WEBINAR_UNMUTE_DEMAND_RECEIVED, { webinar, askedBy });
                        }
                    }
                }

                if (webinar.session?.jingleJid) {
                    if (action === 'allow-talk') {
                        internalStep = 'action allow-talk';
                        const participantId = stanzaElem.find('user-id').text();
                        const participant = webinar.session.getParticipantById(participantId);

                        if (participant) {
                            this.logger.infoEvent(`[WebinarService] onAllowTalk (web: ${webinar.id}, part: ${participantId})`, { type: 'stanza', data: stanza });
                            participant.raiseHand = false;
                            participant.allowedToTalk = true;
                            participant.invitedToGoOnStage = true;
                            participant.rxSubject.next(RBEvent.create('raiseHand', false));
                            webinar.updateRaiseHandParticipants();
                            webinar.rxParticipantsSubject.next(null);
                            webinar.rxSubject.next(null);
                            if (webinar.isAttendee) {
                                this.webinarService.submitEvent(WebinarServiceEvents.WEBINAR_REQUEST_ATTENDEE_STAGING, { webinar });
                            }
                        }
                    }

                    if (action === 'disable-talk') {
                        internalStep = 'action disable-talk';
                        const participantId = stanzaElem.find('user-id').text();
                        const participant = webinar.session.getParticipantById(participantId);
                        if (participant) {
                            const speakerIndex = webinar.session.speakerParticipants.findIndex((partp: WebinarSessionParticipant) => { return partp.id === participantId; });
                            this.logger.infoEvent(`[WebinarService] onDisableTalk (web: ${webinar.id}, part: ${participantId}) ${speakerIndex !== -1 ? '-- remove from publisher list' : ''}`, { type: 'stanza', data: stanza });
                            participant.setMedia(WebinarSessionParticipantMedia.AUDIO, false, true);
                            participant.setMedia(WebinarSessionParticipantMedia.VIDEO, false);
                            participant.setMedia(WebinarSessionParticipantMedia.SHARING, false, true);
                            participant.allowedToTalk = false;
                            participant.invitedToGoOnStage = undefined;
                            participant.rxSubject.next(null);
                            if (webinar.isAttendee) {
                                this.contactService.setBusyState('dnd', 'audio');
                                this.webinarService.submitEvent(WebinarServiceEvents.WEBINAR_REQUEST_ATTENDEE_UNSTAGING, { webinar });
                            }
                            if (speakerIndex !== -1) webinar.session.speakerParticipants.splice(speakerIndex, 1);
                        }
                    }
                }
            }

            if (webinar.session?.localParticipant) {

                // Handle session add/update participants event
                let participantAction: string = 'NewParticipant';
                let participantElems = stanzaElem.find('participants').find('participant');
                if (participantElems.length === 0) { participantElems = stanzaElem.find('added-participants').find('participant'); participantAction = 'AddParticipant'; }
                if (participantElems.length === 0) { participantElems = stanzaElem.find('updated-participants').find('participant'); participantAction = 'UpdateParticipant'; }

                if (participantElems.length) {
                    let shouldEvaluateStage: boolean = false;
                    internalStep = participantAction;
                    const participantsPromise = [];
                    participantElems.each((__i: number, participantElem: Element) => { participantsPromise.push(this.createSessionParticipantFromElem($(participantElem))); });
                    const participants: WebinarSessionParticipant[] = await Promise.all(participantsPromise);
                    participants.forEach((participant: WebinarSessionParticipant) => {
                        if (participant) {
                            webinar.session.addOrUpdateParticipant(participant, participantAction);
                            if (!shouldEvaluateStage && this.isParticipantAnActor(webinar, participant)) shouldEvaluateStage = true;
                            this.logger.infoEvent(`[WebinarService] on${participantAction} (web: ${webinar.id}, part: ${participant.id}, mute: ${participant.mute}, role: ${participant.role})`, { type: 'stanza', data: stanza });
                        }
                    });
                    webinar.rxParticipantsSubject.next(null);
                    //if (shouldEvaluateStage) this.resendStageEvents(webinar);
                }

                // Handle session remove participant event
                const removedParticipantElems = stanzaElem.find('removed-participants');
                if (removedParticipantElems.length) {
                    internalStep = 'removed-participant';
                    const participantIdElems = $(removedParticipantElems).find('user-id');
                    participantIdElems.each((__i: number, participantIdElem: Element) => {
                        const participantId = $(participantIdElem).text();
                        const participant = webinar.session.getParticipantById(participantId);
                        if (participant) {
                            participant.setMedia(WebinarSessionParticipantMedia.AUDIO, false);
                            participant.setMedia(WebinarSessionParticipantMedia.VIDEO, false);
                            participant.setMedia(WebinarSessionParticipantMedia.SHARING, false);
                            this.audioMixer.removeAudioStream(participant, 'all');
                        }

                        const speakerIndex = webinar.session.speakerParticipants.findIndex((partp: WebinarSessionParticipant) => { return partp.id === participantId; });
                        if (speakerIndex !== -1) webinar.session.speakerParticipants.splice(speakerIndex, 1);

                        const attendeeIndex = webinar.session.attendeeParticipants.findIndex((partp: WebinarSessionParticipant) => { return partp.id === participantId; });
                        if (attendeeIndex !== -1) webinar.session.attendeeParticipants.splice(attendeeIndex, 1);

                        this.logger.infoEvent(`[WebinarService] onRemovedParticipant (web: ${webinar.id}, part: ${participantId})`, { type: 'stanza', data: stanza });

                        if (webinar.session.localParticipant && participantId === webinar.session.localParticipant.id) {
                            //if (webinar.isAttendee || webinar.status !== WebinarStatus.IN_PROGRESS) this.clearWebinar(webinar);
                            this.clearWebinar(webinar);
                            webinar.session.localParticipant = null;
                        }
                    });

                    if (!webinar.session.localParticipant && webinar.session.speakerParticipants.length === 0 && webinar.status !== WebinarStatus.IN_PROGRESS) { webinar.session = null; }
                    webinar.updateRaiseHandParticipants();
                    webinar.rxParticipantsSubject.next(null);
                    webinar.rxSubject.next(null);
                }

                // Handle publishers
                let publisherMode: string = 'AddPublishers';
                let publisherElems = stanzaElem.find('publishers');
                if (publisherElems.length === 0) { publisherElems = stanzaElem.find('added-publishers'); publisherMode = 'AddPublisher'; }

                if (publisherElems.length) {
                    internalStep = 'publishers';
                    let shouldEvaluateStage = false;
                    publisherElems.find('publisher').each((__index: number, publisher: any) => {
                        const publisherElem = $(publisher);
                        const publisherId = publisherElem.find('user-id').text();

                        if (publisherId === webinar.session.localParticipant.id) return;
                        const mediaType = publisherElem.find('media-type').text();
                        const participant = webinar.session.getParticipantById(publisherId);
                        if (!participant) {
                            // Handle very special MCU BOT case
                            if (this.connectedUser.jid.startsWith("mcu_"))
                                this.logger.infoEvent(`[WebinarService] on${publisherMode} (web: ${webinar.id}, part: ${publisherId}, media: ${mediaType}) -- IGNORED`, { type: 'stanza', data: stanza });
                            else this.logger.error(`[WebinarService] on${publisherMode} (web: ${webinar.id}, part: ${publisherId}, media: ${mediaType}) -- participant not found in session`, { type: 'stanza', data: stanza });
                        }
                        else {
                            this.logger.infoEvent(`[WebinarService] on${publisherMode} (web: ${webinar.id}, part: ${publisherId}, media: ${mediaType})`, { type: 'stanza', data: stanza });
                            participant.availableStreams[mediaType === 'sharing' ? 'sharing' : 'audioVideo'] = true;
                            if (!this.webinarExtendedBackstage) this.subscribeToPublisher(webinar, participant, mediaType === 'sharing' ? 'sharing' : 'audioVideo');
                            else {
                                participant.sendEvent(WebinarSessionParticipantEvents.UPDATE_MEDIA, { type: mediaType === 'sharing' ? 'sharing' : 'video' });
                                webinar.rxParticipantsSubject.next(null);
                            }

                            if (participant.role === 'attendee') {
                                if (webinar.session.speakerParticipants.find((speaker: WebinarSessionParticipant) => { return speaker.id === participant.id; }) === undefined) {
                                    webinar.session.speakerParticipants.push(participant);
                                    webinar.rxParticipantsSubject.next(null);
                                    participant.sendEvent(WebinarSessionParticipantEvents.UPDATE_PARTICIPANT);
                                }
                            }
                            if (!shouldEvaluateStage && this.isParticipantAnActor(webinar, participant)) shouldEvaluateStage = true;
                        }
                    });
                    if (shouldEvaluateStage) this.resendStageEvents(webinar);
                }

                const removedPublisherElems = stanzaElem.find('removed-publishers');
                if (removedPublisherElems.length) {
                    internalStep = 'removed-publishers';
                    removedPublisherElems.find('publisher').each((__index: number, publisher: any) => {
                        const publisherElem = $(publisher);
                        const publisherId = publisherElem.find('user-id').text();
                        const participant = webinar.session.getParticipantById(publisherId);
                        const mediaType = publisherElem.find('media-type').text();
                        if (participant) {
                            this.logger.infoEvent(`[WebinarService] onRemovePublisher (web: ${webinar.id}, part: ${publisherId}, media: ${mediaType})`, { type: 'stanza', data: stanza });
                            if (mediaType === 'sharing') {
                                this.audioMixer.removeAudioStream(participant, 'sharing');
                                participant.setMedia(WebinarSessionParticipantMedia.SHARING, false);
                                participant.sharingOnStage = false;
                                participant.availableStreams.sharing = false;
                            }
                            else {
                                this.audioMixer.removeAudioStream(participant, 'audio');
                                participant.setMedia(WebinarSessionParticipantMedia.AUDIO, false);
                                participant.setMedia(WebinarSessionParticipantMedia.VIDEO, false);
                                participant.availableStreams.audioVideo = false;
                            }

                            participant.setMedia(mediaType as WebinarSessionParticipantMedia, false);
                            if (webinar.session.masterMedia === `${participant.id}_${mediaType}`) { webinar.session.masterMedia = null; }
                            const speakerIndex = webinar.session.speakerParticipants.findIndex((speaker: WebinarSessionParticipant) => { return speaker.id === participant.id; });
                            if (speakerIndex !== -1) {
                                const speakerParticipant = webinar.session.speakerParticipants[speakerIndex];
                                if (speakerParticipant.medias.audio === false && speakerParticipant.medias.sharing === false) {
                                    webinar.session.speakerParticipants.splice(speakerIndex, 1);
                                }
                            }
                            participant.rxSubject.next(RBEvent.create(mediaType));
                            participant.sendEvent(WebinarSessionParticipantEvents.UPDATE_MEDIA, { type: mediaType === 'sharing' ? 'sharing' : 'video' });

                            webinar.rxParticipantsSubject.next(null);
                        }
                        else {
                            this.logger.infoEvent(`[WebinarService] onRemovePublisher (web: ${webinar.id}, part: ${publisherId}, media: ${mediaType}) -- Not found`);
                        }
                    });
                }

                // Handle talkers
                const talkersElems = stanzaElem.find('talkers');
                if (talkersElems.length) {
                    internalStep = 'talkers';
                    const talker = talkersElems.find('talker')[0];
                    const talkerId = $(talker).find('user-id').text();
                    const participant = webinar.session.getParticipantById(talkerId);
                    if (participant) {
                        const speakers = [webinar.session.localParticipant].concat(webinar.session.speakerParticipants);
                        speakers.forEach((part: WebinarSessionParticipant) => {
                            if (part.id !== talkerId && part.activeSpeaker) {
                                part.activeSpeaker = false;
                                part.sendEvent(WebinarSessionParticipantEvents.ACTIVE_SPEAKER);
                            }
                        });
                        participant.activeSpeaker = true;
                        participant.sendEvent(WebinarSessionParticipantEvents.ACTIVE_SPEAKER);
                    }
                    this.logger.debugEvent(`[WebinarService] onTalker (web: ${webinar.id}, talker: ${talkerId})`);
                }

                // Handle silent
                const silentElems = stanzaElem.find('silents');
                if (silentElems.length) {
                    internalStep = 'silents';
                    silentElems.find('silent').each((__index: number, silent: any) => {
                        const silentId = $(silent).find('user-id').text();
                        const participant = webinar.session.getParticipantById(silentId);
                        if (participant?.activeSpeaker) {
                            participant.activeSpeaker = false;
                            participant.sendEvent(WebinarSessionParticipantEvents.ACTIVE_SPEAKER);
                        }
                        this.logger.debugEvent(`[WebinarService] onSilent (web: ${webinar.id}, talker: ${silentId})`);
                    });
                }

                // Handle raiseHand
                const raiseHandsElems = stanzaElem.find('raise-hands');
                if (raiseHandsElems.length) {
                    internalStep = 'raise-hands';
                    const raiseHandElems = $(raiseHandsElems[0]).find('raise-hand');
                    if (raiseHandElems.length) {
                        raiseHandElems.each((__index: number, raiseHand: any) => {
                            const participantId = $(raiseHand).find('user-id').text();
                            const date = $(raiseHand).find('date').text();
                            const participant = webinar.session.getParticipantById(participantId);
                            participant.raiseHand = true;
                            participant.raiseHandDate = new Date(date);
                            participant.rxSubject.next(RBEvent.create('raiseHand', true));
                            webinar.updateRaiseHandParticipants();
                            webinar.rxSubject.next(null);
                        });
                    }
                }

                // Handle attendees
                const attendeesElems = stanzaElem.find('attendees');
                if (attendeesElems.length) {
                    internalStep = 'attendees';
                    const attendeesPromise = [];
                    attendeesElems.find('attendee-jid').each((__index: number, attendeeElem: any) => {
                        attendeesPromise.push(this.createSessionParticipantFromAttendeeJid($(attendeeElem).text()));
                    });
                    const attendees: WebinarSessionParticipant[] = await Promise.all(attendeesPromise);
                    attendees.forEach((attendee: WebinarSessionParticipant) => {
                        if (attendee) {
                            webinar.session.addOrUpdateParticipant(attendee);
                            this.logger.infoEvent(`[WebinarService] onAddAttendee (web: ${webinar.id}, part: ${attendee.id})`, { type: 'stanza', data: stanza });
                        }
                    });
                    webinar.rxParticipantsSubject.next(null);
                }

                // Handle stage
                const stageElems = stanzaElem.find('stage');
                if (stageElems.length) {
                    internalStep = 'handleStage';
                    const correlatorId = Math.random().toString(36).slice(2, 12);
                    this.logger.infoEvent(`[WebinarService] onStageUpdate (web: ${webinar.id}, correlatorId: ${correlatorId})`, { type: 'stanza', data: stanza });
                    await this.handleStage(webinar, stanzaElem);
                    this.logger.debugEvent(`[WebinarService] onStageUpdate (web: ${webinar.id}, correlatorId: ${correlatorId})`);
                }

                // Handle remoteParticipant audioConfigs (aka "private-communications")
                const audioConfigElems = stanzaElem.find('private-communications');
                if (audioConfigElems.length) {

                    // Get localParticipant
                    const localParticipant = webinar.session.localParticipant;

                    // Handle audioConfigs
                    for (const audioConfigElem of audioConfigElems.find('private-communication').toArray()) {
                        const remoteParticipantId = $(audioConfigElem).find('user-id').text();
                        const audioConfig = $(audioConfigElem).find('listener').text();

                        // Ignore localParticipant audioConfig
                        if (remoteParticipantId === localParticipant.id) continue;

                        // Get the remote participant
                        let remoteParticipant = webinar.session.getParticipantById(remoteParticipantId);

                        // If localParticipant is an attendee allowed to talk the remote participant can not exist.
                        // Indeed an attendee only subscribes to speakers audio. In this case we have to create it.
                        if (webinar.isAttendee && localParticipant.allowedToTalk && !remoteParticipant) {
                            this.logger.infoEvent(`[WebinarService] onAudioConfigUpdate (web: ${webinar.id}, part: ${remoteParticipantId}, audioConfig: ${audioConfig}) -- create remoteParticipant`, { type: 'stanza', data: stanza });
                            remoteParticipant = WebinarSessionParticipant.create(remoteParticipantId);
                            remoteParticipant.contact = await this.contactService.getContactByDBId(remoteParticipantId);
                            webinar.session.speakerParticipants.push(remoteParticipant);
                        }
                        else this.logger.infoEvent(`[WebinarService] onAudioConfigUpdate (web: ${webinar.id}, part: ${remoteParticipantId}, audioConfig: ${audioConfig})`, { type: 'stanza', data: stanza });

                        // Store remoteParticipant audioConfig
                        if (!remoteParticipant) continue;
                        remoteParticipant.audioConfig = audioConfig;
                        remoteParticipant.sendEvent(WebinarSessionParticipantEvents.UPDATE_PARTICIPANT);

                        // Handle audioConfig
                        this.handleRemoteParticipantAudioConfig(webinar, remoteParticipant);
                    }
                }
            }
            return true;
        }
        catch (error) {
            this.logger.error(`[WebinarService] onConferenceMessage -- ${internalStep} -- ${error.message}`, { type: 'stanza', data: stanza });
            return true;
        }
    }

    private async handleRemoteParticipantAudioConfig(webinar: Webinar, remoteParticipant: WebinarSessionParticipant): Promise<void> {
        // Support backstage audio MCU
        if (this.webinarExtendedBackstage) return this.handleRemoteParticipantAudioConfigMCU(webinar, remoteParticipant);

        // Check parameters
        const localParticipant = webinar.session.localParticipant;
        if (!localParticipant || !remoteParticipant?.audioConfig) return;

        // Get audioConfig from remoteParticipant
        let audioConfig = remoteParticipant.audioConfig;

        // Handle onStage case
        if (localParticipant.onStage) {

            // Handle the case where audioConfig is a participantId (if not me -> consider as backstage)
            // if (!audioConfig.includes('stage')) audioConfig = remoteParticipant.audioConfig === localParticipant.contact.dbId ? 'onlyMe' : 'backstage';

            // Remote participant is staged (always unmute staged speaker sound)
            if (remoteParticipant.onStage) this.audioMixer.unmute(remoteParticipant, 'audio');

            // Remote participant is backstaged (check audioConfig)
            else {
                switch (audioConfig) {
                    case 'backstage':
                        // if (webinar.isAttendee && remoteParticipant.medias['audio'] && localParticipant.id !== remoteParticipant.id) {
                        //     this.logger.info(`[WebinarService] handleAudioConfig (web: ${webinar.id}, localParticipant: ${localParticipant.id}, remoteParticipant: ${remoteParticipant.id}) -- terminateJingleSession`);
                        //     const session = remoteParticipant.audioVideoSession;
                        //     this.webrtcConfigAndUtils.disableAllStreamsForSession(session);
                        //     this.terminateJingleSession(session.sid, 'nosound');
                        //     this.audioMixer.removeAudioStream(remoteParticipant, 'audio');
                        //     remoteParticipant.audioVideoSession = remoteParticipant.audioVideoStream = null;
                        //     remoteParticipant.medias.audio = remoteParticipant.medias.video = false;
                        //     const remoteParticipantIndex = webinar.session.speakerParticipants.findIndex((partp: WebinarSessionParticipant) => { return partp.id === remoteParticipant.id; });
                        //     if (remoteParticipantIndex !== -1) webinar.session.speakerParticipants.splice(remoteParticipantIndex, 1);
                        // }

                        //do not cut the session, only mute it;
                        //the session will be removed when the attendee leaves the stage;
                        this.audioMixer.mute(remoteParticipant, 'audio');
                        break;
                    default:
                        if (webinar.isAttendee && !remoteParticipant.medias['audio'] && localParticipant.id !== remoteParticipant.id) {
                            this.logger.info(`[WebinarService] handleAudioConfig (web: ${webinar.id}, localParticipant: ${localParticipant.id}, remoteParticipant: ${remoteParticipant.id}) -- subscribeToPublisher`);
                            await this.subscribeToPublisher(webinar, remoteParticipant, 'audioVideo');
                        }
                        this.audioMixer.unmute(remoteParticipant, 'audio');
                        break;
                }
            }
        }

        // Handle backStage case
        else {
            // Handle the case where audioConfig is a participantId (if not me -> consider as stage)
            if (!audioConfig.includes('stage')) audioConfig = remoteParticipant.audioConfig === localParticipant.contact.dbId ? 'onlyMe' : 'stage';
            switch (audioConfig) {
                case 'backstage': this.audioMixer.unmute(remoteParticipant, 'audio'); break;
                case 'stage': this.audioMixer.unmute(remoteParticipant, 'audio'); break;
                default: this.audioMixer.mute(remoteParticipant, 'audio'); break;
            }
        }
    }

    private async handleRemoteParticipantAudioConfigMCU(webinar: Webinar, remoteParticipant: WebinarSessionParticipant): Promise<void> {
        // Check parameters
        const localParticipant = webinar.session.localParticipant;
        if (!localParticipant || !remoteParticipant?.audioConfig) return;

        // Get audioConfig from remoteParticipant
        const audioConfig = remoteParticipant.audioConfig;

        // Handle onStage case
        if (localParticipant?.onStage) {

            // Manage backstage audioP
            if (!this.webinarExtendedBackstage) { this.audioMixer.manageBackstageMic(webinar, false); }
            this.audioMixer.muteBackstage(webinar);

            // Remote participant is staged (always unmute staged speaker sound)
            if (remoteParticipant?.onStage) this.audioMixer.unmute(remoteParticipant, 'audio');

            // Remote participant is backstaged (check audioConfig)
            else {
                switch (audioConfig) {
                    case "stage":
                        if (!remoteParticipant.medias.audio) {
                            this.logger.info(`[WebinarService] handleAudioConfig (web: ${webinar.id}, localParticipant: ${localParticipant.id}, remoteParticipant: ${remoteParticipant.id}) -- subscribeToPublisher`);
                            await webinar.subscribeToPublisher(remoteParticipant, 'audioVideo');
                        }
                        this.audioMixer.unmute(remoteParticipant, 'audio');
                        break;
                    case 'backstage':
                        this.logger.info(`[WebinarService] handleAudioConfig (web: ${webinar.id}, localParticipant: ${localParticipant.id}, remoteParticipant: ${remoteParticipant.id}) -- unsubscribeToPublisher`);
                        if (!remoteParticipant.trackedBackstage && !remoteParticipant.sharingOnStage)
                            webinar.unsubscribeToPublisher(remoteParticipant, 'audioVideo', true);
                        if (webinar.isAttendee && !remoteParticipant.sharingOnStage) {
                            const remoteParticipantIndex = webinar.session.speakerParticipants.findIndex((partp: WebinarSessionParticipant) => { return partp.id === remoteParticipant.id; });
                            if (remoteParticipantIndex !== -1) webinar.session.speakerParticipants.splice(remoteParticipantIndex, 1);
                        }
                        this.audioMixer.mute(remoteParticipant, 'audio');
                        break;
                }
            }
        }

        // Handle backStage case
        else {
            // Manage backstage audio
            if (!this.webinarExtendedBackstage) this.audioMixer.manageBackstageMic(webinar, !localParticipant.mute);
            this.audioMixer.unmuteBackstage(webinar);

            // Handle stage audio
            if (remoteParticipant.onStage) this.audioMixer.unmute(remoteParticipant, 'audio');
            else {
                switch (audioConfig) {
                    case 'backstage': this.audioMixer.mute(remoteParticipant, 'audio'); break;
                    default: this.audioMixer.mute(remoteParticipant, 'audio'); break;
                }
            }
        }
    }

    private handleRecording(webinar: Webinar, stanzaElem: any): void {
        const recordingElems = stanzaElem.find('recording');
        if (recordingElems.length) webinar.recorded = recordingElems.text() === 'true' ? 'true' : 'false';
    }

    private async handleStage(webinar: Webinar, stanzaElem: any): Promise<any> {
        const correlatorId = Math.random().toString(36).slice(2, 12);
        try {
            this.logger.info(`[WebinarService] handleStage (web: ${webinar.id}, correlatorId: ${correlatorId})`);

            const stageElems = stanzaElem.find('stage');
            if (stageElems.length && webinar.session) {

                // Extract info from stage message
                const actorsInfo: any = { audioVideo: {}, sharing: {}, mix: {} };
                const actorElems = stanzaElem.find('actor');
                if (actorElems.length) {
                    actorElems.each((__index: number, actor: any) => {
                        const actorElem = $(actor);
                        const type = actorElem.find('type').text() === 'participant' ? 'audioVideo' : actorElem.find('type').text();
                        const participantId = actorElem.find('user-id').text();
                        const state = actorElem.find('master').text() === 'true' ? 'master' : 'staged';
                        actorsInfo[type][participantId] = state;
                    });
                }

                // !!!!!!! TEMPORAIRE: REMOVE MIX BOT FROM PARTICIPANTS ARRAY !!!!!!
                const mixBotId = Object.keys(actorsInfo['mix'])[0];
                if (mixBotId) delete actorsInfo['audioVideo'][mixBotId];

                webinar.lastActorsInfo = actorsInfo;
                await this.updateStage(webinar, actorsInfo);
                this.logger.debug(`[WebinarService] handleStage (web: ${webinar.id}, correlatorId: ${correlatorId}) -- success`);
            }
        }
        catch (error) {
            this.logger.error(`[WebinarService] handleStage (web: ${webinar.id}, correlatorId: ${correlatorId}) -- ${error.message}`);
        }
    }

    private async updateStage(webinar: Webinar, actorsInfo: any, snapshot: boolean = false): Promise<void> {

        let shouldUnmute: boolean = false;

        // Reset masterMedia information
        webinar.session.masterMedia = null;

        // Get local particpant
        const localParticipant = webinar.session.localParticipant;
        if (!localParticipant?.id) {
            this.logger.warn(`[WebinarService] updateStage (web: ${webinar.id}) -- no localParticipant found - ignored`);
            return;
        }

        // Complete actorsInfo
        const speakers = webinar.session.speakerParticipants.concat([localParticipant]);
        speakers.forEach((speaker: WebinarSessionParticipant) => {
            if (speaker !== null) {
                if (!actorsInfo.audioVideo[speaker.id]) actorsInfo.audioVideo[speaker.id] = 'offStage';
                if (!actorsInfo.sharing[speaker.id]) actorsInfo.sharing[speaker.id] = 'offStage';
            }
        });

        // Check if localParticipant was on backstage an goes on stage
        if (!localParticipant?.onStage && actorsInfo.audioVideo[localParticipant.id] !== "offStage") {
            this.webinarService.conference.audioMixer.setAudioBalance(0.2);
            shouldUnmute = true;
        }

        // Check if localParticipant was on stage an returns backstage (change audioConfig to 'backstage')
        if (localParticipant?.onStage && actorsInfo.audioVideo[localParticipant.id] === "offStage") {
            localParticipant.audioConfig = 'backstage';
            localParticipant.sendEvent(WebinarSessionParticipantEvents.UPDATE_PARTICIPANT, "returnBackstage");
            await webinar.shareLocalParticipantAudioConfig(localParticipant.audioConfig);
            shouldUnmute = !localParticipant.mute;
            this.webinarService.conference.audioMixer.setAudioBalance(0.8);
        }

        // Reset current speakers stage information
        speakers.forEach((speaker: WebinarSessionParticipant) => { if (speaker !== null) speaker.onStage = speaker.sharingOnStage = false; });

        // Build flat actors array from actorsInfo maps
        let nbActorsOnStage: number = 0;
        let onStageActorId: string;

        //this.logger.error(`[WebinarService] updateStage -- ${JSON.stringify(actorsInfo, null, 2)}`);

        let actors = ['audioVideo', 'sharing'].reduce((actorArrays: any[], media: string) => {
            Object.keys(actorsInfo[media]).map((participantId: any) => {
                const state = actorsInfo[media][participantId];
                actorArrays.push({ participantId, media, state });
                if (state === 'master' || state === 'staged') nbActorsOnStage += 1;
                if (state === 'staged') onStageActorId = participantId;
            });
            return actorArrays;
        }, []);

        // HACK MULTIPLE SUBSCRIPTIONS
        if (config.webinarMultiSubscribe) {
            webinar.isMCUBOT = true;
            const masterIndex = actors.findIndex(actor => actor.state === "master");
            if (actors[masterIndex].state === "master") {
                actors[masterIndex].state = "staged";
                for (let i = 0; i < 99; i++) {
                    actors.push({ participantId: `dummy-${i}-${actors[masterIndex].participantId}`, media: 'audioVideo', state: "staged" });
                }
            }
        }

        // Handle attendee videoMCU special case, replace actors array by a single mcuActor
        if (webinar.isAttendee) {
            const mcuBotId = Object.keys(actorsInfo['mix'])[0];
            if (mcuBotId) {
                this.logger.info(`[WebinarService] updateStage (web: ${webinar.id}, part: ${mcuBotId}, mcu: true)`);
                actors = [{ participantId: mcuBotId, media: 'audioVideo', state: 'master' }];
                let mcuBotParticipant = webinar.session.getParticipantById(mcuBotId);
                if (!mcuBotParticipant) {
                    mcuBotParticipant = WebinarSessionParticipant.create(mcuBotId);
                    mcuBotParticipant.contact = await this.contactService.getContactByDBId(mcuBotId);
                }
                mcuBotParticipant.type = WebinarSessionParticipantType.MCUBOT;
                mcuBotParticipant.audioConfig = "stage";
            }
        }

        // Handle case where only 1 actor is onStage
        if (nbActorsOnStage === 1 && onStageActorId) {
            const actor: any = actors.find((actor: any) => { return actor.participantId === onStageActorId && actor.state === "staged"; });
            if (actor) actor.state = 'master';
        }

        // Handle all actors
        await this.utilitiesService.asyncForEach(actors, async (actor: any) => {
            try {
                const participantId: string = actor.participantId;
                const state = actor.state;
                const media = actor.media;
                let participant = webinar.session.getParticipantById(participantId);

                // On stage case : create missing speaker/participant (if needed) in case of attendee or snapshot
                if (state !== 'offStage' && !participant && (webinar.isAttendee || webinar.isMCUBOT || snapshot)) {
                    this.logger.info(`[WebinarService] updateStage (web: ${webinar.id}, part: ${participantId}, media: ${media}, state: ${state}) -- no existing participant -- create it`);
                    participant = WebinarSessionParticipant.create(participantId);

                    try {
                        if (config.webinarMultiSubscribe && participantId.startsWith('dummy-')) participant.contact = this.contactService.createBasicContact(undefined, participantId);
                        else participant.contact = await this.contactService.getContactByDBId(participantId);
                    }
                    catch (error) {
                        participant.contact = this.contactService.createBasicContact(participantId);
                        this.logger.warn(`[WebinarService] updateStage -- failed to get actor contact ${actor.participantId}: ${stringifyError(error)}`);
                    }

                    // Sometimes we create several times the same participant, so recheck before adding him again
                    if (!webinar.session.getParticipantById(participantId)) {
                        webinar.session.speakerParticipants.push(participant);
                        webinar.rxParticipantsSubject.next(null);
                        participant.rxSubject.next(RBEvent.create('updateParticipant'));
                    }
                }

                // Update participant stage state
                if (participant) {
                    this.logger.info(`[WebinarService] updateStage (web: ${webinar.id}, part: ${participantId}, media: ${media}, state: ${state})`);
                    participant[media === 'audioVideo' ? 'onStage' : 'sharingOnStage'] = state !== 'offStage';
                    participant.invitedToGoOnStage = undefined;

                    // On stage case : update master media and subscribe to publisher if necessary
                    if (state !== 'offStage') {
                    // Update master media
                        if (state === 'master') webinar.session.masterMedia = `${participantId}_${media === 'audioVideo' ? 'participant' : 'sharing'}`;

                        // Subscribe to publisher
                        if (localParticipant.id !== participant.id && !participant.medias[media === 'audioVideo' ? 'audio' : 'sharing']) {
                            // For speaker wait the addPublisher before subscribing
                            if (!webinar.isAttendee && !participant.availableStreams?.[media]) {
                                this.logger.info(`[WebinarService] updateStage (web: ${webinar.id}, part: ${participantId}, media: ${media}, state: ${state}) -- publisher not yet available, skip subscription`);
                            }
                            else {
                                this.logger.info(`[WebinarService] updateStage (web: ${webinar.id}, part: ${participantId}, media: ${media}, state: ${state}) -- subscribe to publisher`);
                                await this.subscribeToPublisher(webinar, participant, media);
                            }
                        }

                        if (participant.allowedToTalk) {
                            participant.audioConfig = 'stage';
                        }
                    }

                    /* **  NOT ON STAGE ** */
                    else {
                    // Handle attendee
                        if (webinar.isAttendee && participant.medias[media === 'audioVideo' ? 'audio' : 'sharing']) {
                            const session = media === 'audioVideo' ? participant.audioVideoSession : participant.sharingSession;
                            this.webrtcConfigAndUtils.disableAllStreamsForSession(session);

                            // Remove myself (as attendee allowed to talk)
                            if (participant.isLocalParticipant()) {
                                if (media === 'audioVideo') this.removeAudioVideoMedia(webinar);
                                else this.removeLocalMedia(webinar, 'sharing');
                            }
                            // Terminate session (leaveStage) with speaker on stage
                            else {
                                this.logger.info(`[WebinarService] updateStage (web: ${webinar.id}, part: ${participantId}, media: ${media}, state: ${state}) -- terminate session (leave stage)`);
                                this.terminateSession(participant, session.sid, 'leaveStage');
                            }

                            // Clean statistics, medias, sessions and streams
                            this.stopStatsForSession(session);
                            if (media === 'audioVideo') {
                                this.audioMixer.removeAudioStream(participant, 'audio');
                                participant.setMedia(WebinarSessionParticipantMedia.AUDIO, false, true);
                                participant.setMedia(WebinarSessionParticipantMedia.VIDEO, false);
                            }
                            else participant.setMedia(WebinarSessionParticipantMedia.SHARING, false, true);
                        }

                        // Handle remote speakers (regulars and allow to talk attendees)
                        if (!participant.isLocalParticipant()) {
                            const session = media === 'audioVideo' ? participant.audioVideoSession : participant.sharingSession;
                            if (session) {
                                this.webrtcConfigAndUtils.disableAllStreamsForSession(session);
                                this.logger.info(`[WebinarService] updateStage (web: ${webinar.id}, part: ${participantId}, media: ${media}, state: ${state}) -- terminate session (leave stage)`);

                                if (!this.webinarExtendedBackstage || participant.trackedBackstage) {
                                // Keep the session alive, cause media is still displayed on the backstage
                                }
                                else {
                                // Terminate session with 'leaveStage' reason
                                    this.terminateSession(participant, session.sid, 'leaveStage');

                                    // Clean statistics, medias, sessions and streams
                                    this.stopStatsForSession(session);
                                    if (media === 'audioVideo') {
                                        this.audioMixer.removeAudioStream(participant, 'audio');
                                        participant.setMedia(WebinarSessionParticipantMedia.AUDIO, false, true);
                                        participant.setMedia(WebinarSessionParticipantMedia.VIDEO, false);
                                    }
                                    else participant.setMedia(WebinarSessionParticipantMedia.SHARING, false, true);
                                }
                            }
                        }
                    }

                    // Send update participant event (listened by stage cells)
                    const eventType = { type: (media === 'audioVideo' ? 'video' : 'sharing') };
                    participant.sendEvent(WebinarSessionParticipantEvents.UPDATE_MEDIA, eventType);
                }
            }
            catch (error) {
                this.logger.warn(`[WebinarService] updateStage -- failed to handle actor ${actor.participantId}: ${stringifyError(error)}`);
            }
        });

        // Handle audio
        actors.forEach((actor) => {
            if (actor.participantId === webinar.session.localParticipant.id) return;
            const remoteParticipant = webinar.session.getParticipantById(actor.participantId);
            this.handleRemoteParticipantAudioConfig(webinar, remoteParticipant);
            this.audioMixer.updateAudioLevelForParticipant(remoteParticipant);
        });

        // unmute localParticpant (MCU) if necessary (stage to backstage)
        if (shouldUnmute) await webinar.muteParticipant(localParticipant, false);

        webinar.rxParticipantsSubject.next(null);
    }

    private async getWebinarSnapshot(webinar: Webinar, trial: number = 1): Promise<void> {
        try {
            this.logger.info(`[WebinarService] getWebinarSnapshot (web: ${webinar.id}, trial: ${trial})`);
            const url = `${config.restServerUrl}/api/rainbow/conference/v1.0/rooms/${webinar.roomId}/snapshot`;
            const headers = this.authService.getRequestHeader();
            const response = await fetch(url, { method: 'GET', headers });
            if (!response.ok) throw response;
            const data = (await response.json()).data;

            webinar.session.masterMedia = null;
            webinar.session.speakerParticipants.forEach((speaker: WebinarSessionParticipant) => { speaker.onStage = speaker.sharingOnStage = false; });
            webinar.session.connected = data.isConnected;

            // Handle status
            if (data && data.active === false) webinar.status = WebinarStatus.TERMINATED;

            // Handle stage
            const actorsInfo: any = { audioVideo: {}, sharing: {}, mix: {} };
            if (data?.stage?.actors) {
                const actors = data.stage.actors;
                actors.forEach((actor: any) => {
                    const participantId = actor.userId;
                    const type = actor.type === 'participant' ? 'audioVideo' : actor.type;
                    const staged = actor.master ? 'master' : 'staged';
                    actorsInfo[type][participantId] = staged;
                });
            }

            // !!!!!!! TEMPORAIRE: REMOVE MIX BOT FROM PARTICIPANTS ARRAY !!!!!!
            const mixBotId = Object.keys(actorsInfo['mix'])[0];
            if (mixBotId) delete actorsInfo['audioVideo'][mixBotId];

            this.updateStage(webinar, actorsInfo, true);
        }
        catch {
            if (trial === 5) throw new Error(`[WebinarService] getWebinarSnapshot (web: ${webinar.id}) -- failure after ${trial} trials`);
            await wait(1000);
            await this.getWebinarSnapshot(webinar, ++trial);
        }
    }

    public async subscribeToPublisher(webinar: Webinar, participant: WebinarSessionParticipant, media: string, force: boolean = false, retry: boolean = true): Promise<any> {
        const correlatorId = Math.random().toString(36).slice(2, 12);
        // Already have an audioVideo session
        if (participant.audioVideoSession && media === 'audioVideo' && !force) {
            this.logger.error(`[WebinarService] subscribeToPublisher (web: ${webinar.id}, part: ${participant.id}, type: ${media}) -- ignored, session already exists`);
            return null;
        }

        // Already have a sharing session
        if (participant.sharingSession && media === 'sharing' && !force) {
            this.logger.error(`[WebinarService] subscribeToPublisher (web: ${webinar.id}, part: ${participant.id}, type: sharing) -- ignored, session already exists`);
            return null;
        }

        if (!this.subscribeToPublisherPromiseResolvers[`${webinar.id}_${participant.id}_${media}`]) {
            this.subscribeToPublisherPromiseResolvers[`${webinar.id}_${participant.id}_${media}`] = new Promise<any>((resolve: any, reject: any) => {
                const mediaToLog = (media === 'audioVideo') ? 'audio+video' : 'sharing';
                if (webinar.session.speakerParticipants.find((speaker: WebinarSessionParticipant) => { return speaker.id === participant.id; }) === undefined) {
                    this.logger.info(`[WebinarService] subscribeToPublisher (web: ${webinar.id}, part: ${participant.id}, type: ${mediaToLog}, correlatorId: ${correlatorId}) -- add to publisher list`);
                    webinar.session.speakerParticipants.push(participant);
                    webinar.rxParticipantsSubject.next(null);
                    participant.rxSubject.next(RBEvent.create('updateParticipant'));
                }
                else this.logger.info(`[WebinarService] subscribeToPublisher (web: ${webinar.id}, part: ${participant.id}, type: ${mediaToLog}, correlatorId: ${correlatorId})`);

                this.subscribeToPublisherInternal(webinar, participant, media, retry)
                    .then((response: any) => {
                        delete this.subscribeToPublisherPromiseResolvers[`${webinar.id}_${participant.id}_${media}`];
                        participant[media === 'sharing' ? 'sharingSessionId' : 'audioVideoSessionId'] = response.sid;
                        this.logger.debug(`[WebinarService] subscribeToPublisher (web:${webinar.id}, part: ${participant.id}, type: ${mediaToLog}, sid: ${response.sid}, requestId: ${response.requestId}, correlatorId: ${correlatorId}) -- success`);
                        resolve();
                    })
                    .catch((error) => {
                        reject(error);
                        delete this.subscribeToPublisherPromiseResolvers[`${webinar.id}_${participant.id}_${media}`];
                    });
            });
        }
        else this.logger.info(`[WebinarService] subscribeToPublisher (web: ${webinar.id}, part: ${participant.id}, type: ${media}, correlatorId: ${correlatorId}) -- ignored, already in progress`);
        return this.subscribeToPublisherPromiseResolvers[`${webinar.id}_${participant.id}_${media}`];
    }

    private async subscribeToPublisherInternal(webinar: Webinar, participant: WebinarSessionParticipant, media: string, retry: boolean): Promise<{ sid: string; requestId: string; }> {
        try {
            const participantId = (config.webinarMultiSubscribe && participant.id.startsWith('dummy-')) ? participant.id.split('-')[2] : participant.id;
            const url = `${config.restServerUrl}/api/rainbow/conference/v1.0/rooms/${webinar.roomId}/users/${participantId}/subscribe`;
            const headers = this.authService.getPostHeader();
            const bodyObj: any = { media };
            if (media === 'audioVideo') media = 'audio+video';
            if (retry) this.addSubscribeToPublisherTimeout(webinar, participant, media);

            const body = JSON.stringify(bodyObj);
            const response = await fetch(url, { method: 'PUT', headers, body });
            const requestId = response.headers.get("x-rainbow-request-id");

            if (!response.ok) throw response;
            const data = (await response.json()).data;
            media.split('+').forEach((amedia: string) => { participant.setMedia(amedia as WebinarSessionParticipantMedia, true); });
            this.logger.debug(`[WebinarService] subscribeToPublisherInternal (web: ${webinar.id}, part:${participant.id}, type: ${media}) -- success -- sid: ${data.sid}`);
            return { requestId, sid: data.sid };
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, "subscribeToPublisher");
            this.logger.error(`[WebinarService] subscribeToPublisherInternal (web: ${webinar.id}, part:${participant.id}, type: ${media}) -- failure -- ${errorInfo.error.message}`);
            if (errorInfo.error.errorDetailsCode === 503006 || errorInfo.error.errorDetailsCode === 403092) {
                await wait(1000);
                const mediaFix = (media === 'audio+video') ? 'audioVideo' : media;
                this.clearSubscribeToPublisherTimeout(webinar, participant, mediaFix);
                if (retry) return await this.subscribeToPublisherInternal(webinar, participant, mediaFix, false);
            }
            else throw errorInfo.error;
        }
    }

    public unsubscribeToPublisher(webinar: Webinar, participant: WebinarSessionParticipant, media: string, disableStream?: boolean): Promise<void> {

        // Get the participant session
        let session: JingleSession;
        switch (media) {
            case 'audio': case 'video': case 'audioVideo': session = participant.audioVideoSession; break;
            case 'sharing': session = participant.sharingSession; break;
            default: break;
        }
        if (!session) return;

        // Disable all streams
        if (disableStream === true) this.webrtcConfigAndUtils.disableAllStreamsForSession(session);

        // Terminate the session
        this.terminateSession(participant, session.sid, 'unsubscribeToPublisher');

        // Clean media, session and stream
        switch (media) {
            case 'audio':
            case 'video':
            case 'audioVideo':
                this.audioMixer.removeAudioStream(participant, 'audio');
                participant.setMedia(WebinarSessionParticipantMedia.AUDIO, false, true);
                participant.setMedia(WebinarSessionParticipantMedia.VIDEO, false);
                break;
            case 'sharing':
                participant.setMedia(WebinarSessionParticipantMedia.SHARING, false, true);
                break;
            default: break;
        }

        // Stop session stats
        this.stopStatsForSession(session);
    }

    private async createSessionParticipantFromElem(participantElem: any): Promise<WebinarSessionParticipant> {
        try {
            const contactId = participantElem.find('user-id').text();
            const contact: Contact = await this.contactService.getContactByDBId(contactId);
            if (contact) {
                const sessionParticipant = WebinarSessionParticipant.create(contactId);
                sessionParticipant.contact = contact;
                sessionParticipant.mute = participantElem.find('mute').text() === 'true';
                sessionParticipant.role = participantElem.find('role').text();
                return sessionParticipant;
            }
            return null;
        }
        catch {
            this.logger.error(`[WebinarService] createSessionParticipantFromParticipantElem -- Failure`);
            return null;
        }
    }

    private async createSessionParticipantFromAttendeeJid(attendeeJid: string): Promise<WebinarSessionParticipant> {
        try {
            const contact: Contact = await this.contactService.getOrCreateContact(attendeeJid);
            if (contact) {
                const sessionParticipant = WebinarSessionParticipant.create(contact.dbId);
                sessionParticipant.contact = contact;
                sessionParticipant.mute = true;
                sessionParticipant.role = 'attendee';
                return sessionParticipant;
            }
            return null;
        }
        catch {
            this.logger.error(`[WebinarService] createSessionParticipantFromAttendeeElem -- Failure`);
            return null;
        }
    }

    private onVideoServiceEvent(event: RBEvent): void {
        try {
            if (event.name === 'ON_CHROME_STOP_SCREEN_SHARING_TRIGGERED') {
                const webinar = event.data.webinar;
                if (webinar) { this.removeLocalMedia(webinar, 'sharing'); }
            }
        }
        catch { /**/ }
    }

    private async onSessionMessage(event: RBEvent): Promise<void> {
        try {
            const webinar = this.webinarService.getWebinarBySessionId(event.data.sid, event.data.confId);
            if (webinar) {
                switch (event.name) {
                    case 'jingleSessionInitiate': this.onSessionInitiate(webinar, event.data.sid); break;
                    case 'jingleConnectionStateChange': await this.onSessionStateChange(webinar, event.data.session); break;
                    case 'jingleSessionTerminate': this.onSessionTerminated(webinar, event.data); break;
                    case 'jingleContentAdd': this.onJingleContentAdd(webinar, event.data.sid, event.data.mediaType); break;
                    case 'jingleContentModify': this.onJingleContentModify(webinar, event.data.sid, event.data.medias); break; // jingleUpdateStream
                    case 'jingleMediaModified': this.onJingleMediaModified(webinar, event.data.sid); break;
                    case 'jingleContentRemove': this.onJingleContentRemove(webinar, event.data.sid); break;
                    case 'webrtcSessionStats.jingle': this.onJingleStatsChange(webinar, event.data); break;
                    case 'resubscriptionNeeded': this.onJingleResubscriptionNeededEvent(webinar, event.data.sid); break;
                    case 'checkSubscriptionStatus': this.onJingleCheckSubscriptionStatus(webinar, event.data.sid); break;
                    case 'jingleError': await this.onJingleErrorEvent(webinar, event); break;
                    case 'transceiverStats': /* Ignore message */ break;
                    case 'webrtcStatsEvent.jingle': /* Ignore message */ break;
                    default: this.onSessionMessageDefaultHandler(webinar, event); break;
                }

                //by default, mark all sessions as WEBINARonSessionMessageDefaul
                const session = this.xmppService.connection.jingle.sessions[event.data.sid];
                if (session) session.isWebinarSession = true;
            }
        }
        catch (error) {
            this.logger.error(`[WebinarService] onSessionMessage (sid: ${event.data.sid}, evt: ${event.name}) -- ${error?.message}`);
        }
    }

    private async onSessionStateChange(webinar: Webinar, session: JingleSession): Promise<any> {

        if (!session?.peerconnection) {
            this.logger.infoEvent(`[WebinarService] onSessionStateChange (web: ${webinar?.id}, sid: ${session?.sid}) -- peerConnection does not exist anymore`);
            return;
        }

        const sessionParticipant = webinar.session.getParticipantBySession(session);

        if (session.peerconnection.signalingState !== "stable") {
            this.logger.debugEvent(`[WebinarService] onSessionStateChange (web: ${webinar?.id}, part: ${sessionParticipant?.id}, sid: ${session?.sid}, signalingState: ${session.peerconnection.signalingState}, iceConnectionState: ${session.peerconnection.iceConnectionState}, connectionState: ${session.peerconnection.connectionState}) -- event is ignored as signalingState is not stable`);
            return;
        }

        this.logger.debugEvent(`[WebinarService] onSessionStateChange (web: ${webinar?.id}, part: ${sessionParticipant?.id}, sid: ${session?.sid}, type: ${session.localType}, signalingState: ${session.peerconnection.signalingState}, iceConnectionState: ${session.peerconnection.iceConnectionState}, connectionState: ${session.peerconnection.connectionState})`);

        // Consider the "ICE" connectionState
        switch (session.peerconnection.iceConnectionState) {

            //case 'connecting': TODO ASK DOBRO ????
            case "checking":
                // Start timer to check if session is blocked after 15 sec
                setTimeout(() => {
                    if (webinar.session && session && session.peerconnection) {
                        const newState = session.peerconnection.connectionState ? session.peerconnection.connectionState : session.peerconnection.iceConnectionState;
                        if (newState === "connected" || newState === "completed") return;
                        this.logger.debugEvent(`[WebinarService] onSessionConnecting (web:${webinar?.id}, part: ${sessionParticipant?.id}, sid:${session?.sid}) -- session is blocked with connectionState ${newState}`);

                        // Local session reconnection
                        const localParticipant = webinar.session?.localParticipant;
                        if (localParticipant) {
                            const sessionType = localParticipant.getSessionType(session.sid);
                            if (sessionType !== null) this.sendTransportReplaceForSession(webinar, session, 0);
                        }

                        // Remote session reconnection
                        const participant = webinar.session?.getSpeakerBySessionId(session.sid);
                        if (participant) {
                            this.terminateSession(participant, session.sid, 'sessionConnectionFailed');
                            this.stopStatsForSession(session);
                            this.audioMixer.removeAudioStream(participant, 'audio');
                            participant.audioVideoSession = participant.audioVideoStream = undefined;
                            participant.audioVideoSessionId = undefined;
                            setTimeout(() => {
                                this.subscribeToPublisher(webinar, participant, session.localType === 'audio+video' ? 'audioVideo' : 'sharing');
                            }, 500);
                        }
                    }
                }, 15000);
                break;

            case "disconnected":
                this.logger.debugEvent(`[WebinarService] onSessionDisconnected (web:${webinar?.id},  part: ${sessionParticipant?.id}, sid:${session?.sid}, state: ${session.peerconnection.iceConnectionState}) -- start reconnect mecanism`);
                //TODO Mathieu: do the same thing as for ICE failed, but after 2-3 sec timeout;
                break;

            case "failed":
                // MCO: GROS PROBLEM LA, JE NETTOIE TOUT ALORS QUE SEULE UNE SESSION EXPLOSE...
                // C'EST DEBILE
                this.logger.debugEvent(`[WebinarService] onSessionConnectionFailed (web:${webinar?.id}, part: ${sessionParticipant?.id}, sid:${session?.sid}, state: ${session.peerconnection.iceConnectionState}) -- start reconnect mecanism`);
                if (this.connected) {
                    // Local session reconnection
                    const localParticipant = webinar.session.localParticipant;
                    if (localParticipant) {
                        this.stopStatsForAllSessions();
                        this.clearSubscribeToPublisherTimeouts();
                        const sessionType = localParticipant.getSessionType(session.sid);
                        if (sessionType !== null) this.scheduleTransportReplace(webinar, session, 500);
                    }

                    // Remote session reconnection
                    const participant = webinar.session.getSpeakerBySessionId(session.sid);
                    if (participant) {
                        this.terminateSession(participant, session.sid, `icefailed`);
                        this.scheduleSubscribeToPublisher(webinar, participant, session, false, 500);
                    }
                }
                else {
                    this.stopStatsForAllSessions();
                    this.clearSubscribeToPublisherTimeouts();
                }
                break;

            default: break;
        }

        // Consider the connectionState
        const connectionState = session.peerconnection.connectionState;
        if (connectionState === 'connected') {

            if (this.webinarExtendedBackstage && session.localType === 'audio') {
                webinar.session.mcuAudioSession = session;
                if (session.remoteStreamsObject?.["0"]) {
                    this.logger.infoEvent(`[WebinarService] onSessionConnected (web: ${webinar?.id}, sid: ${session?.sid}, media: audio, part: ${webinar.session?.localParticipant?.id})`);
                    this.audioMixer.addBackstageAudio(webinar, this.webinarExtendedBackstage);
                }
                this.startStatsForSession(session, 5000, webinar);
            }

            if (session.localType === 'audio+video') {

                // Handle local participant events
                if (!session.publisherId) {
                    const localParticipant = webinar.session.localParticipant;
                    if (!localParticipant.audioVideoSession) {
                        const participantId = this.connectedUser.dbId;
                        localParticipant.audioVideoSession = session;
                        this.startStatsForSession(session, 5000, webinar);

                        // Case 1 : the connected user is an attendee allowed to talk
                        // Should unmute and put on stage
                        // Not subscribe to other publisher (already subscribed)
                        if (webinar.isAttendee) {
                            try {
                                this.logger.infoEvent(`[WebinarService] onSessionConnected (web: ${webinar?.id}, sid: ${session?.sid}, media: audio+video, part: ${localParticipant?.id}) -- put attendee on stage`);
                                const participant = webinar.session.getParticipantById(participantId);
                                await webinar.muteParticipant(participant, localParticipant.mute);
                                await webinar.putLocalParticipantAudioVideoMediaOnStage();
                                localParticipant.allowedToTalk = true;
                            }
                            catch (error) {
                                this.webrtcConfigAndUtils.disableAllStreamsForSession(session);
                                this.removeAudioVideoMedia(webinar);
                                localParticipant.allowedToTalk = false;
                                await webinar.disableAttendeeToTalk(this.connectedUser.dbId);
                                this.activeWebinar.rxInfoSubject.next(RBEvent.create('error', 'webinarAttendeePutOnStageError'));
                                this.logger.error(`[WebinarService] onSessionConnected (web: ${webinar?.id}, sid: ${session?.sid}) -- ${stringifyError(error)}`);
                            }
                        }

                        // Case 2 : the connected user is a moderator or speaker
                        // Update media list from stream
                        // Subscribe to other speakers
                        else {
                            this.logger.infoEvent(`[WebinarService] onSessionConnected (web: ${webinar?.id}, sid: ${session?.sid}, media: audio+video, part: ${localParticipant?.id}) -- moderator or speaker`);
                            const participant = webinar.session.getParticipantById(participantId);
                            if (!this.webinarExtendedBackstage) await webinar.muteParticipant(participant, true);

                            // Update video media from established stream
                            const audioVideoStream = localParticipant.audioVideoStream;
                            if (audioVideoStream) {
                                const mediaInfo = session.webinarGetMedia();
                                if (!mediaInfo.video) localParticipant.setMedia(WebinarSessionParticipantMedia.VIDEO, false);
                            }
                        }
                    }
                    this.setBusyState(localParticipant);
                    localParticipant.sendEvent(WebinarSessionParticipantEvents.UPDATE_MEDIA, { type: 'video' });
                }

                else {
                    // Handle remote participant events
                    const participant = webinar.session.getParticipantBySession(session);

                    if (!participant.isLocalParticipant()) {
                        const stream: MediaStream = session.remoteStreamsObject?.["0"] ? session.remoteStreamsObject["0"] : null;
                        if (!stream) return;
                        if (!participant) this.logger.error(`[WebinarService] onSessionConnected (web: ${webinar?.id}, sid: ${session?.sid}, media: audio+video, part: ${participant?.id}) -- addParticipant -- participant not found`);
                        else {
                            await wait(100);
                            participant.audioVideoSession = session;
                            this.startStatsForSession(session);
                            participant.audioVideoStream = stream;
                            let media = 'audio+video';

                            // Extract media info from remoteSDP
                            const mediaInfo = session.webinarGetMedia();
                            if (mediaInfo.video === false) {
                                participant.setMedia(WebinarSessionParticipantMedia.VIDEO, false);
                                media = 'audio';
                            }

                            this.audioMixer.addAudioStream(webinar, participant, 'audio', participant.medias.video ? '-video' : '');

                            // FOR SPEAKER ONLY: mute this participant audio if
                            // 1) the localParticipant is staged
                            // 2) the remoteParticipant is backstaged AND the remoteParticipant.audioConfig is not 'stage'

                            if (!webinar.isAttendee) {
                                if (webinar.session?.localParticipant?.onStage && !participant.onStage && participant.audioConfig !== 'stage') {
                                    this.audioMixer.mute(participant, "all");
                                }
                                if (!webinar.session?.localParticipant?.onStage && !participant.onStage) {
                                    if (this.webinarExtendedBackstage) {
                                        this.audioMixer.mute(participant, "all");
                                    }
                                }
                            }
                            webinar.rxParticipantsSubject.next(RBEvent.create('addParticipant', participant));
                            this.logger.infoEvent(`[WebinarService] onSessionConnected (web: ${webinar?.id}, sid: ${session?.sid}, media: audio+video, stream:${media}, part: ${participant?.id}, videoMuted: ${!mediaInfo.video}) -- addParticipant`);
                            if (webinar.incomingStreamsDisabled.video && participant.medias.video) this.pauseVideoStream(webinar, participant, 'video', true, 'addParticipant');
                            participant.sendEvent(WebinarSessionParticipantEvents.UPDATE_MEDIA, { type: 'video' });
                        }
                    }
                }
            }
            else if (session.localType === 'sharing') {
                // Handle local participant events
                if (!session.publisherId) {
                    this.logger.infoEvent(`[WebinarService] onSessionConnected (web: ${webinar?.id}, sid: ${session?.sid}, media: sharing, part: ${webinar.session?.localParticipant?.id})`);
                    this.setBusyState(webinar.session.localParticipant);
                    if (webinar.isAttendee) await webinar.putParticipantSharingMediaOnStage(webinar.session.localParticipant, true);
                    this.startStatsForSession(session);
                    webinar.session.localParticipant.sendEvent(WebinarSessionParticipantEvents.UPDATE_MEDIA, { type: 'sharing' });
                }
                // Handle remote participant events
                else {
                    const participant = webinar.session.getParticipantBySession(session);
                    const stream: MediaStream = session.remoteStreamsObject?.["0"] ? session.remoteStreamsObject["0"] : null;
                    if (!stream) return;
                    participant.setMedia(WebinarSessionParticipantMedia.SHARING, true);
                    participant.sharingSession = session;
                    this.startStatsForSession(session);
                    participant.sharingStream = stream;
                    this.audioMixer.addAudioStream(webinar, participant, 'sharing');
                    participant.sendEvent(WebinarSessionParticipantEvents.UPDATE_MEDIA, { type: 'sharing' });
                    if (webinar.incomingStreamsDisabled.sharing) this.pauseVideoStream(webinar, participant, 'sharing', true, 'addParticipant');
                    this.logger.infoEvent(`[WebinarService] onSessionConnected (web: ${webinar?.id}, sid: ${session?.sid}, media: sharing, part: ${participant?.id})`);
                }
                webinar.rxParticipantsSubject.next(null);
            }
        }
    }

    private scheduleSubscribeToPublisher(webinar: Webinar, participant: WebinarSessionParticipant, session: any, force: boolean = false, delay: number = 4000): void {
        if (session && participant) {
            const peerConnection = session.peerconnection;
            if (peerConnection) {
                const peerConnectionState = peerConnection.connectionState ? peerConnection.connectionState : peerConnection.iceConnectionState;
                if (peerConnection.signalingState === "stable" && (peerConnectionState === 'failed' || peerConnectionState === 'disconnected')) {
                    this.logger.info(`[WebinarService] scheduleSubscribeToPublisher(web: ${webinar.id}, sid: ${session.sid}, type: ${session.localType}, state: ${peerConnectionState})`);
                    setTimeout(() => {
                        this.subscribeToPublisher(webinar, participant, session.localType === 'audio+video' ? 'audioVideo' : 'sharing', force);
                    }, delay);
                }
            }
            else {
                this.logger.info(`[WebinarService] scheduleSubscribeToPublisher(web: ${webinar.id}, type: ${session.localType})`);
                setTimeout(() => {
                    this.subscribeToPublisher(webinar, participant, session.localType === 'audio+video' ? 'audioVideo' : 'sharing', force);
                }, delay);
            }
        }
    }

    private scheduleTransportReplace(webinar: Webinar, session: any, delay?: number): void {
        if (session?.peerconnection) {
            const peerConnection = session.peerconnection;
            const peerConnectionState = peerConnection.connectionState ? peerConnection.connectionState : peerConnection.iceConnectionState;
            if (peerConnection.signalingState === "stable" && (peerConnectionState === 'failed' || peerConnectionState === 'disconnected')) {
                if (!delay) delay = peerConnectionState === 'failed' ? 500 : (session.localType === 'audio+video' ? 5000 : 8000);
                this.sendTransportReplaceForSession(webinar, session, delay);
                this.logger.info(`[WebinarService] scheduleTransportReplace (web: ${webinar.id}, sid: ${session.sid}, type: ${session.localType}, state: ${peerConnectionState})-- in ${delay} ms`);
            }
        }
    }

    private sendTransportReplaceForSession(webinar: Webinar, session: any, timeout: number = 1000): void {
        if (this.reconnectingSessionIds[session.sid]) {
            this.logger.info(`[WebinarService] sendTransportReplaceForSession(web: ${webinar.id}, sid: ${session.sid}, type: ${session.localType})-- already reconnecting`);
            return;
        }
        this.reconnectingSessionIds[session.sid] = true;

        setTimeout(() => {
            this.reconnectingSessionIds[session.sid] = false;
            delete this.reconnectingSessionIds[session.sid];

            if (this.connected && session && session.peerconnection && session.peerconnection.signalingState === "stable" && (session.peerconnection.iceConnectionState === "failed" || session.peerconnection.iceConnectionState === "disconnected")) {
                this.logger.info(`[WebinarService] sendTransportReplaceForSession(web: ${webinar.id}, sid: ${session.sid}, type: ${session.localType})`);
                session.connection = this.xmppService.connection;
                session.reconnectSession();
            }
            else { this.logger.info(`[WebinarService] sendTransportReplaceForSession(web: ${webinar.id}, sid: ${session.sid}, type: ${session.localType}) -- not send`); }
        }, timeout);
    }

    private terminateSession(participant: WebinarSessionParticipant, sessionId: string, reason?: string): void {
        this.logger.info(`[WebinarService] terminateSession (web: ${this.activeWebinar.id}, part: ${participant.id}, sid: ${sessionId}, reason: ${reason})`);
        this.xmppService.connection.jingle.terminate(sessionId, reason);
    }

    private onSessionInitiate(webinar: Webinar, sessionId: string) {
        const session = this.xmppService.connection.jingle.sessions[sessionId];
        session.localType = session.remoteType;
        session.sendAnswer();
        const participant = webinar.session.getParticipantById(session.publisherId);
        this.clearSubscribeToPublisherTimeout(webinar, participant, session.localType);
        this.logger.infoEvent(`[WebinarService] onSessionInitiate (web: ${webinar.id}, sid: ${session.sid}, type: ${session.localType}) -- accept`);
    }

    private onSessionTerminated(webinar: Webinar, eventData: any) {
        const sessionId = eventData.sid;
        const reason = eventData.reason;
        const reasonText = eventData.reasonText;

        if (!webinar?.session) {
            this.logger.infoEvent(`[WebinarService] onSessionTerminated (web: ${webinar.id}, sid: ${sessionId}, reason: ${reason} ${reasonText}) -- no session`);
            return;
        }
        else this.logger.infoEvent(`[WebinarService] onSessionTerminated (web: ${webinar.id}, sid: ${sessionId}, reason: ${reason} ${reasonText})`);

        // LocalParticipant session terminated
        const localParticipant = webinar.session?.localParticipant;
        if (localParticipant) {
            const sessionType = localParticipant.getSessionType(sessionId);
            if (sessionType) {
                this.logger.infoEvent(`[WebinarService] onSessionTerminated (web: ${webinar.id}, sid: ${sessionId}, media: ${sessionType}, part: ${localParticipant.id}, reason: ${reason} ${reasonText})`);
                if (sessionType === 'audio+video') {
                    if (webinar.isAttendee) {
                        localParticipant.onStage = false;
                    }
                    else {
                        this.clearWebinar(webinar);
                        webinar.session.localParticipant = null;
                    }
                    return;
                }
            }
        }

        // Speaker session terminated
        const speaker = webinar.session.getSpeakerBySessionId(sessionId);
        if (speaker) {
            const sessionType = speaker.getSessionType(sessionId);
            if (sessionType) {
                this.logger.infoEvent(`[WebinarService] onSessionTerminated (web: ${webinar.id}, sid: ${sessionId}, media: ${sessionType}, part: ${speaker.id}, reason: ${reason} ${reasonText})`);
                if (sessionType === 'audio+video') {
                    this.stopStatsForSession(speaker.audioVideoSession);
                    this.audioMixer.removeAudioStream(speaker, 'audio');
                    speaker.setMedia(WebinarSessionParticipantMedia.AUDIO, false, true);
                    speaker.setMedia(WebinarSessionParticipantMedia.VIDEO, false);
                    speaker.rxSubject.next(RBEvent.create('video'));
                    if (reason === 'streamer down') {
                        this.logger.infoEvent(`[WebinarService] onSessionTerminated (web: ${webinar.id}, sid: ${sessionId}, media: ${sessionType}, part: ${speaker.id}, reason: ${reason} ${reasonText}) -- resubscribe`);
                        this.subscribeToPublisher(webinar, speaker, 'audioVideo', true, true);
                    }
                }
                if (sessionType === 'sharing') {
                    speaker.setMedia(WebinarSessionParticipantMedia.SHARING, false, true);
                    speaker.rxSubject.next(RBEvent.create('sharing'));
                    if (reason === 'streamer down') {
                        this.logger.infoEvent(`[WebinarService] onSessionTerminated (web: ${webinar.id}, sid: ${sessionId}, media: ${sessionType}, part: ${speaker.id}, reason: ${reason} ${reasonText}) -- resubscribe`);
                        this.subscribeToPublisher(webinar, speaker, 'sharing', true, true);
                    }
                }
                webinar.rxParticipantsSubject.next(null);
            }
        }
    }

    private onJingleContentAdd(webinar: Webinar, sessionId: string, mediaType: string): void {
        // Receives mediaType : 'audio' or 'audio+video' -- only remove video in fact -- so log video info
        this.logger.infoEvent(`[WebinarService] onJingleContentAdd (web: ${webinar.id}, sid: ${sessionId}, type: video)`);
        const session = this.xmppService.connection.jingle.sessions[sessionId];
        if (session?.publisherId) {
            const participant = webinar.session.getParticipantById(session.publisherId);
            const stream: MediaStream = session.remoteStreamsObject?.["0"] ? session.remoteStreamsObject["0"] : null;

            // Only handle audio/video session no content add on sharing session
            if (stream && session.localType === 'audio+video') {
                this.stopStatsForSession(participant.audioVideoSession);
                participant.audioVideoSession = session;
                this.startStatsForSession(participant.audioVideoSession);
                participant.audioVideoStream = stream;
                participant.setMedia(WebinarSessionParticipantMedia.AUDIO, true);
                participant.setMedia(WebinarSessionParticipantMedia.VIDEO, mediaType === 'audio+video');
                participant.sendEvent(WebinarSessionParticipantEvents.UPDATE_MEDIA, { type: 'video' });
            }
        }
    }

    private onJingleContentModify(webinar: Webinar, sessionId: string, medias: any): void {
        this.logger.infoEvent(`[WebinarService] onJingleContentModify (web: ${webinar.id}, sid: ${sessionId})`);

        const session = this.xmppService.connection.jingle.sessions[sessionId];
        if (session?.publisherId) {
            const participant = webinar.session.getParticipantById(session.publisherId);
            const stream: MediaStream = session.remoteStreamsObject?.["0"] ? session.remoteStreamsObject["0"] : null;
            if (stream) {
                if (session.localType === 'audio+video') {
                    this.stopStatsForSession(participant.audioVideoSession);
                    participant.audioVideoSession = session;
                    this.startStatsForSession(participant.audioVideoSession);
                    participant.audioVideoStream = stream;
                    participant.setMedia(WebinarSessionParticipantMedia.AUDIO, medias.audio);
                    participant.setMedia(WebinarSessionParticipantMedia.VIDEO, medias.video);
                    participant.sendEvent(WebinarSessionParticipantEvents.UPDATE_MEDIA, { type: 'video' });
                }
                else {
                    participant.sharingSession = session;
                    participant.sharingStream = stream;
                    participant.setMedia(WebinarSessionParticipantMedia.SHARING, true);
                    participant.sendEvent(WebinarSessionParticipantEvents.UPDATE_MEDIA, { type: 'sharing' });
                }
            }
        }
    }

    private onJingleMediaModified(webinar: Webinar, sessionId: string): void {
        const session = this.xmppService.connection.jingle.sessions[sessionId];
        if (session?.publisherId) {
            const participant = webinar.session.getParticipantById(session.publisherId);
            if (session.localType === 'audio+video') {
                const mediaInfo = session.webinarGetMedia();
                participant.setMedia(WebinarSessionParticipantMedia.VIDEO, mediaInfo.video);
                this.logger.infoEvent(`[WebinarService] onJingleMediaModified (web: ${webinar.id}, sid: ${sessionId}), session: audio+video, video: ${participant.medias.video}`);
                participant.sendEvent(WebinarSessionParticipantEvents.UPDATE_MEDIA, { type: 'video' });
            }
        }
    }

    private onJingleContentRemove(webinar: Webinar, sessionId: string): void {
        // Receives mediaType : 'audio' or 'audio+video' -- only remove video in fact -- so log video info
        this.logger.infoEvent(`[WebinarService] onJingleContentRemove (web: ${webinar.id}, sid: ${sessionId}, type: video)`);
        const session = this.xmppService.connection.jingle.sessions[sessionId];
        if (session?.publisherId) {
            const participant = webinar.session.getParticipantById(session.publisherId);
            const stream: MediaStream = session.remoteStreamsObject?.["0"] ? session.remoteStreamsObject["0"] : null;

            // Only handle audio/video session no content remove on sharing session
            if (stream && session.localType === 'audio+video') {
                this.stopStatsForSession(participant.audioVideoSession);
                participant.audioVideoSession = session;
                this.startStatsForSession(participant.audioVideoSession);
                participant.audioVideoStream = stream;
                participant.setMedia(WebinarSessionParticipantMedia.VIDEO, false);
                participant.sendEvent(WebinarSessionParticipantEvents.UPDATE_MEDIA, { type: 'video' });
            }
        }
    }

    private setBusyState(participant: WebinarSessionParticipant): void {
        if (participant.role === 'attendee') this.contactService.setBusyState('dnd', 'audio');
        if (participant?.medias) {
            if (participant.medias.sharing) this.contactService.setBusyState('dnd', 'sharing');
            else if (participant.medias.video) this.contactService.setBusyState('dnd', 'video');
            else if (participant.medias.audio) this.contactService.setBusyState('dnd', 'audio');
        }
    }

    private onJingleStatsChange(webinar: Webinar, stats: any): void {
        this.logger.infoOrange(`webinar stats ${webinar.id} ${stats} `);
    }

    private onJingleCheckSubscriptionStatus(webinar: Webinar, sessionId: string): void {
        const session = this.xmppService.connection.jingle.sessions[sessionId];
        if (session?.publisherId) {
            const participant = webinar.session.getParticipantById(session.publisherId);

            //ignore
            if (!participant || participant.role === 'attendee' || participant.mute || !this.connected || webinar.isAttendee || webinar.isGuestAttendee || webinar.session?.localParticipant?.role === 'attendee') return;

            //participant is a speaker, and is not muted, we need to resubscribe to his sessions
            this.logger.infoEvent(`[WebinarService] onJingleCheckSubscriptionStatus -- no audio packets received, resubscribe to web: ${webinar.id}, sid: ${sessionId}, part: ${participant.id} for media: audioVideo`);
            this.terminateSession(participant, session.sid, 'leaveStage');
            this.stopStatsForSession(session);
            this.audioMixer.removeAudioStream(participant, 'audio');
            participant.audioVideoSession = participant.audioVideoStream = undefined;
            participant.audioVideoSessionId = undefined;
            setTimeout(() => {
                this.subscribeToPublisher(webinar, participant, 'audioVideo');
            }, 500);
        }
    }

    private onJingleResubscriptionNeededEvent(webinar: Webinar, sessionId: string): void {
        if (config.noAutoResubscribe) return;
        const session = this.xmppService.connection.jingle.sessions[sessionId];
        if (session?.publisherId) {
            const participant = webinar.session.getParticipantById(session.publisherId);
            if (session.localType === 'audio+video') {
                this.logger.infoEvent(`[WebinarService] onJingleResubscriptionNeededEvent (web: ${webinar.id}, sid: ${sessionId}, part: ${participant.id}, media: audioVideo)`);
            }
            else {
                this.logger.infoEvent(`[WebinarService] onJingleResubscriptionNeededEvent (web: ${webinar.id}, sid: ${sessionId}, part: ${participant.id}, media: sharing)`);
            }
        }
    }

    private async onJingleErrorEvent(webinar: Webinar, event: any) {
        const sessionId = event.data.sid;
        if (webinar?.session?.localParticipant) {

            const sessionType = webinar.session.localParticipant.getSessionType(sessionId);
            if (sessionType === 'audio+video') {
                try {
                    this.logger.warn(`[WebinarService] onJingleErrorEvent (web: ${webinar.id}, sid: ${sessionId}) -- error on localParticipant audio+video session`);
                    this.webinarService.conference.removeAudioVideoMedia(webinar);
                    this.webinarService.conference.removeSharingMedia(webinar);
                    webinar.session = null;
                    await this.webinarService.updateWebinarFromServer(webinar);
                    if (webinar.room) await this.bubbleService.updateRoomFromServer(webinar.room);
                    if (webinar.practiceRoom) await this.bubbleService.updateRoomFromServer(webinar.practiceRoom);
                    webinar.rxInfoSubject.next(RBEvent.create('error', 'webinarDisconnectedMoreThan90s'));
                }
                catch (error) {
                    this.logger.error(`[WebinarService] onJingleErrorEvent (web: ${webinar.id}, sid: ${sessionId}) -- ${error.message}`);
                }
            }
            else if (sessionType === 'sharing') { /* Do nothing for the moment */ }
            else {
                let errorMessage = 'Unknown';
                if (event?.error) errorMessage = `${event?.error.reason} ${event?.error.source}`;
                this.logger.error(`[WebinarService] onJingleErrorEvent (web: sid: ${sessionId}) -- ${errorMessage}`);
            }
        }
    }

    private async onSessionMessageDefaultHandler(webinar: Webinar, event: any) {
        try {
            const sessionId = event.data.sid;
            const session = this.xmppService.connection.jingle.sessions[sessionId];
            const participant = webinar.session.getParticipantBySession(session);
            switch (event.name) {
                case "createOffer.jingle":
                    this.logger.debugEvent(`[WebinarService] onSessionCreateOffer (web: ${webinar.id}, part: ${participant.id}, sid: ${event.data.sid})`);
                    break;
                case "setLocalDescription.jingle":
                    const data = session.localSDP.raw;
                    this.logger.debugEvent(`[WebinarService] onSessionSetLocalDescription (web: ${webinar.id}, part: ${participant.id}, sid: ${event.data.sid})`, { type: 'sdp', data });
                    break;
                case "setRemoteDescription.jingle":
                    const remoteData = session.remoteSDP.raw;
                    this.logger.debugEvent(`[WebinarService] onSessionSetRemoteDescription (web: ${webinar.id}, part: ${participant.id}, sid: ${event.data.sid})`, { type: 'sdp', data: remoteData });
                    break;
                case "onicecandidateerror":
                    this.logger.debugEvent(`[WebinarService] onICECandidateError (web: ${webinar.id}, part: ${participant.id}, sid: ${event.data.sid}, type: ${event.data.type}, info: ${event.data.info})`);
                    break;
                default:
                    this.logger.debugEvent(`[WebinarService] onSessionMessage (web: ${webinar.id}, part: ${participant.id}, sid: ${event.data.sid}, evt: ${event.name})`);
                    break;
            }
        }
        catch { /**/ }
    }

    private onXmppConnectionChangeEvent(event: RBEvent): void {
        try {
            const webinar = this.webinarService.conference.activeWebinar;

            this.logger.infoEvent(`[WebinarService] onXmppConnectionChangeEvent (web: ${webinar.id}, event: ${event.name}, reason: ${event.data ? event.data.reason : 'UNKNOWN'})`);
            if (event.name === 'ON_XMPP_DISCONNECTED') { this.connected = false; }
            else if (event.name === 'ON_XMPP_CONNECTED' && event.data.reason === 'RECONNECTION') {
                this.connected = true;
            }
            else if (event.name === 'ON_XMPP_CONNECTED' && event.data.reason === 'PRESENCE') {
                this.connected = true;
                this.attachXmppListeners();
            }
        }
        catch { /**/ }
    }

    private async onReplaceBackgroundChangeEvent(event: RBEvent): Promise<void> {
        try {
            const webinar = this.webinarService.conference.activeWebinar;
            if (webinar.session?.localParticipant?.medias.video) {
                this.logger.infoEvent(`[WebinarService] onReplaceBackgroundChangeEvent`);
                webinar.session.localParticipant.audioVideoStream = event.data.stream;
                const tracks = event.data.stream.getTracks();
                if (tracks.length === 2) { webinar.session.localParticipant.audioVideoSession.replaceVideoTrack(tracks[1]); }
                webinar.session.localParticipant.sendEvent(WebinarSessionParticipantEvents.UPDATE_MEDIA, { type: 'video' });
            }
        }
        catch { /**/ }
    }

    private async onMediaDeviceChangeEvent(event: RBEvent) {
        try {
            if (event.name === DeviceManagementEvents.ON_NEW_AUDIO_VIDEO_DEVICE_SELECTED) {
                const kind = event.data.kind;
                this.logger.infoEvent(`[WebinarService] onMediaDeviceChangeEvent(${kind})`);
                const webinar = this.activeWebinar;

                if (kind === 'audiooutput') { await this.audioMixer.updateAudioDevice(); }
                else if (webinar?.session && (webinar.session.localParticipant.medias.audio || webinar.session.localParticipant.medias.video)) {
                    const localParticipant = webinar.session.localParticipant;

                    // Get new stream with new devices
                    const medias: string[] = [];
                    if (localParticipant.medias.audio) medias.push('audio');
                    if (localParticipant.medias.video) medias.push('video');
                    const originalStream = await this.webrtcConfigAndUtils.getBrowserMedia(medias, undefined, undefined, undefined, webinar);

                    // Disable existing stream from originalAudioVideoStream
                    this.webrtcConfigAndUtils.disableMediaStreams([localParticipant.originalAudioVideoStream]);
                    localParticipant.originalAudioVideoStream = originalStream;

                    // Process virtualBackground stream
                    await this.virtualBackgroundService?.stopProcessStream();
                    const modifiedStream = this.virtualBackgroundService ? await this.virtualBackgroundService.startProcessStream(originalStream, 'WebinarConferenceService') : originalStream;

                    try {
                        const tracks = modifiedStream.getTracks();
                        await this.utilitiesService.asyncForEach(tracks, async (track: MediaStreamTrack, index: number) => {
                            await localParticipant.audioVideoSession.replaceTransceiverTrack(track, index);
                        });
                        this.webrtcConfigAndUtils.disableMediaStreams(localParticipant.audioVideoSession.localStreams);
                        localParticipant.audioVideoSession.localStreams = [];
                        localParticipant.audioVideoSession.localStreams.push(modifiedStream);
                        localParticipant.audioVideoStream = modifiedStream;
                        localParticipant.sendEvent(WebinarSessionParticipantEvents.UPDATE_MEDIA, { type: 'video' });
                        this.logger.infoEvent(`[WebinarService] onMediaDeviceChangeEvent (${kind})-- Success 'on the fly'`);
                    }
                    catch {
                        // Change tracks "on the fly" does not work, should renegotiate the session
                        this.webrtcConfigAndUtils.disableMediaStreams(localParticipant.audioVideoSession.localStreams);
                        localParticipant.audioVideoSession.localStreams = [];
                        localParticipant.audioVideoSession.localStreams.push(modifiedStream);
                        await localParticipant.audioVideoSession.updateSessionStreamsAndTransceivers(localParticipant.audioVideoSession.localStreams);
                        localParticipant.audioVideoSession.sendTransportReplace();
                        localParticipant.audioVideoStream = modifiedStream;
                        localParticipant.sendEvent(WebinarSessionParticipantEvents.UPDATE_MEDIA, { type: 'video' });
                        this.logger.infoEvent(`[WebinarService] onMediaDeviceChangeEvent (${kind})-- Success 'via transport-replace'`);
                    }
                }
            }
        }
        catch {
            this.logger.info(`[WebinarService] onMediaDeviceChangeEvent-- Failure`);
        }
    }

    // **************************************************** //
    // **  WEBRTC STATS STUFF                            ** //
    // **************************************************** //
    private startStatsForSession(session: any, delay: number = 5000, webinar?: Webinar): void {
        const webinarId = webinar ? webinar.id : this.activeWebinar?.id;
        const intervalId = this.webrtcConfigAndUtils.getStatsForSession(session, delay, webinarId);
        this.logger.info(`[WebinarService] startStatsForSession (web: ${webinarId}, sid : ${session.sid}, type: ${session.localType} delay: ${delay}, intervalId: ${intervalId})`);
        this.statsIntervals[intervalId.toString()] = intervalId;
    }

    private stopStatsForSession(session: any): void {
        if (session?.webrtcStatsInterval) {
            delete this.statsIntervals[session.webrtcStatsInterval.toString()];
            const webinar = this.activeWebinar;
            this.logger.info(`[WebinarService] stopStatsForSession (web: ${webinar?.id}, sid : ${session.sid}, intervalId: ${session.webrtcStatsInterval})`);
            clearInterval(session.webrtcStatsInterval);
            session.webrtcStatsInterval = null;
        }
    }

    private stopStatsForAllSessions(): void {
        try {
            Object.keys(this.statsIntervals).forEach((key: string) => {
                const intervalId = this.statsIntervals[key];
                if (intervalId) {
                    clearInterval(intervalId);
                    this.logger.info(`[WebinarService] stopStatsSessions (web: ${this.activeWebinar.id}, intervalId: ${intervalId})`);
                }
            });
            this.statsIntervals = {};
        } catch { /**/ }
    }

    // **************************************************** //
    // **  SUSCRIBE TO PUBLISHER TIMEOUT STUFF           ** //
    // **************************************************** //
    private addSubscribeToPublisherTimeout(webinar: Webinar, participant: WebinarSessionParticipant, media: string): void {
        if (config.noSubscribeToPublisherRetry) return;
        const key = `${webinar.id}_${participant.id}_${media}`;
        this.logger.debug(`[WebinarService] addSubscribeToPublisherTimeout (web :${webinar.id}, participant:${participant.id}, media: ${media})`);
        this.subscribeToPublisherTimeouts[key] = {
            webinar,
            participant,
            media,
            timeout: setTimeout(() => {
                this.logger.warn(`[WebinarService] subscribeToPublisherTimeout (web :${webinar.id}, participant:${participant.id}, media: ${media})`);
                this.resubscribe(webinar, participant, media);
            }, 5000)
        };
    }

    private clearSubscribeToPublisherTimeout(webinar: Webinar, participant: WebinarSessionParticipant, media: string): void {
        if (config.noSubscribeToPublisherRetry) return;
        const info = this.subscribeToPublisherTimeouts[`${webinar.id}_${participant.id}_${media}`];
        if (info) {
            clearTimeout(info.timeout);
            delete this.subscribeToPublisherTimeouts[`${webinar.id}_${participant.id}_${media}`];
            this.logger.debug(`[WebinarService] clearSubscribeToPublisherTimeout (web :${webinar.id}, participant:${participant.id}, media: ${media})`);
        }
    }

    private clearSubscribeToPublisherTimeouts(): void {
        if (config.noSubscribeToPublisherRetry) return;
        try {
            this.logger.debug(`[WebinarService] clearSubscribeToPublisherTimeouts()`);
            Object.keys(this.subscribeToPublisherTimeouts).forEach((key: string) => {
                const info = this.subscribeToPublisherTimeouts[key];
                if (info) this.clearSubscribeToPublisherTimeout(info.webinar, info.participant, info.media);
            });
            this.subscribeToPublisherTimeouts = {};
        } catch { /**/ }
    }

    private resubscribe(webinar: Webinar, participant: WebinarSessionParticipant, media: string): void {
        this.logger.warn(`[WebinarService] resubscribe (web :${webinar.id}, participant:${participant.id}, media: ${media})`);
        const session = (media === 'audio+video') ? participant.audioVideoSession : participant.sharingSession;
        if (session) {
            this.webrtcConfigAndUtils.disableAllStreamsForSession(session);
            this.terminateSession(participant, session.sid, 'leaveStage');
            this.stopStatsForSession(session);
        }
        // Clean media, session and stream
        if (media === 'audio+video') {
            this.audioMixer.removeAudioStream(participant, 'audio');
            participant.setMedia(WebinarSessionParticipantMedia.AUDIO, false, true);
            participant.setMedia(WebinarSessionParticipantMedia.VIDEO, false);
            if (participant.onStage) this.subscribeToPublisher(webinar, participant, 'audioVideo', false, false);
        }
        else {
            participant.setMedia(WebinarSessionParticipantMedia.SHARING, false, true);
            if (participant.sharingOnStage) this.subscribeToPublisher(webinar, participant, 'sharing', false, false);
        }
    }

    private clearWebinar(webinar: Webinar): void {
        this.logger.info(`[WebinarService] clearWebinar (web :${webinar.id})`);
        this.audioMixer.destroy();
        const participants = [webinar.session.localParticipant].concat(webinar.session.speakerParticipants, webinar.session.attendeeParticipants);
        participants.forEach((part: WebinarSessionParticipant) => { part.availableStreams = { audioVideo: false, sharing: false }; });
        this.stopStatsForAllSessions();
        this.clearSubscribeToPublisherTimeouts();
        this.removeAudioVideoMedia(webinar);
        this.removeActiveWebinar(webinar);
        this.webinarService.submitEvent(WebinarServiceEvents.WEBINAR_SHARED_SCREEN, { webinar, action: 'stop' });
        if (!webinar.isGuest) this.activeWebinarSubject.next(RBEvent.create('ON_ACTIVE_WEBINAR_DISCONNECTED', { webinar }));
    }

    private clearSession(webinar: Webinar): void {
        if (webinar.session) {
            const speakers = [webinar.session.localParticipant].concat(webinar.session.speakerParticipants);
            speakers.forEach((speaker: WebinarSessionParticipant) => {
                if (speaker?.audioVideoSession) {
                    this.logger.info(`[WebinarService] clearSession (web :${webinar.id}, speaker: ${speaker.id}, session: ${speaker.audioVideoSession.sid})`);
                    this.stopStatsForSession(speaker.audioVideoSession);
                    this.terminateSession(speaker, speaker.audioVideoSession.sid, 'clearSession');
                    speaker.audioVideoSessionId = speaker.audioVideoSession = undefined;
                    speaker.availableStreams.audioVideo = false;
                }
                if (speaker?.sharingSession) {
                    this.logger.info(`[WebinarService] clearSession (web :${webinar.id}, speaker: ${speaker.id}, session: ${speaker.sharingSession.sid})`);
                    this.stopStatsForSession(speaker.sharingSession);
                    this.terminateSession(speaker, speaker.sharingSession.sid, 'clearSession');
                    speaker.sharingSessionId = speaker.sharingSession = undefined;
                    speaker.availableStreams.sharing = false;
                }
            });
        }
    }
}
